// src/components/LessonPage.jsx
import { isStudentView, getStudentView } from "../utils/studentView";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  useOutletContext,
} from "react-router-dom";

// BannerStatus disabled for now
// import BannerStatus from "./BannerStatus";
import FeedbackCard from "./FeedbackCard";
import AdminEvaluationDetails from "./AdminEvaluationDetails";
import FeedbackProgressSlot, { isFeedbackEvaluationActive } from "./FeedbackProgressSlot";
import MicroLessonCard from "./MicroLessonCard";
import WeekIntroPage from "./WeekIntroPage";
import YouTubePlayer from "./YouTubePlayer";
import ThemedAudioPlayer from "./ThemedAudioPlayer";
import HighlightedCreativeText from "./HighlightedCreativeText"; // 👈 NER highlight view
import StoryCurvesSvgChart from "./story/StoryCurvesSvgChart";
import {
  CURRENT_FEEDBACK_UI_VERSION,
  buildSubmissionRecoveryKey,
  getSubmissionRecoveryKeys,
} from "./feedbackUiVersion";

import { getPerformanceBand } from "./lessonPerformanceBand";
import { getLessonFooterCopy } from "./lessonFooterCopy";
import { markCurrentLessonCompleteBeforeReflection } from "./lessonReflectionTransition";
import {
  isKnownStaleW1L1FeedbackPayload,
  shouldRefreshW1L1FeedbackPayload,
} from "./lessonPageFeedbackGate";
import { theMagicBuildingCurves } from "../data/theMagicBuildingCurves";
import "./LessonPage.css";

import { API_BASE_URL } from "../config/apiBase";
import { canonicalLessonId, lessonPathFor } from "../utils/lessonRoute";
import { getAuthUserHeader, isAdminUser, readStoredAuthUser } from "../utils/userRoles";
import { advanceFeedbackProgress, feedbackProgressCap } from "./feedbackProgress";
import { scrollFeedbackProgressIntoView } from "./feedbackProgressScroll";

const capitalizeFirstCharacter = (value) => {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const API = API_BASE_URL;
const MAX_RESPONSE_CHARS = 12000;
const FEEDBACK_PROGRESS_MIN_VISIBLE_MS = 3000;
const FEEDBACK_PROGRESS_POST_COMPLETE_HOLD_MS = 500;
const FEEDBACK_PROGRESS_SLOW_WARNING_MS = 45_000;
const FEEDBACK_PROGRESS_MAX_WAIT_MS = 60_000;
const W4L4_CHART_INTRO_SEEN_KEY = "writeflow_w4l4_chart_intro_seen";
const W4L4_CHART_POINT_COUNT = theMagicBuildingCurves.sections.length;
const W4L4_INTRO_CARD_PHASE = "intro_card";
const W4L4_SECTION_5_REWRITE_PASSAGE = `As we stepped in, a warm draft swept our hair and a moist mist in the room tickled our faces. Baffled and flabbergasted we walked on. The building was blue from the inside……and as we walked something fragile blew against our feet. The floor was covered with bubbles that formed again the instant they got popped! Hundreds of tiny transparent circular soap matters blanketed the floor. I started running. The soapy bubbles made me slip, but as I held on to the wall for support, something caught my eye and caused me to scream in front of Andy; we had introduced ourselves finally. The walls were covered with dancing insects twenty times their normal size! Beetles, ladybirds, butterflies! But they weren’t real. They weren’t puppets. Were they shadows? I looked around to see if there were any projectors or if they were actually in the building, but they were indeed just shadows! Dancing, singing, having a good time. Then the most stupendous thing caught our attention. Sea creatures swimming in mid-air before our eyes. Giants!! So much that plankton was as big as two taxis combined. My heart beat fast and so did Andy’s as he grabbed me by the arm as if for affirmation of that which was real.
We found a power switch, that seemed harmless to operate with a label saying “Push for Fun”. I pushed it and it worked … we could now hear majestic sounds of whales echoing all around us. The squids and whales and sharks and puffer fish bellowed before us and swam through the air. We edged ourselves forward, convinced of their harmlessness when the earth shook beneath our feet. We were carried up in the air by a floating piece of land. Gigantic jellyfish sashayed all around us and their fresh bright colors cast a beam of scintillating magic everywhere. Suddenly I remembered before the closing of the building, grandpa had one last puppet show left. One that was supposed to be the biggest in history. Most fresh, innovative and imaginative. That show never came into production. Ever since we walked in, the building welcomed us and kept us amused and entertained. This was the finale. Grandpas last show. We were the audience it craved for.`;
const WRITING_SIGNAL_MODES = {
  BASIC: "basic",
  ENRICHED: "enriched",
  OFF: "off",
};
const MAGIC_BUILDING_EMOTION_CURVE =
  theMagicBuildingCurves.curves.find((curve) => curve.label === "Emotion")?.values || [];

const EVALUATION_DIMENSIONS = {
  task_completion: {
    label: "Task completion",
    explanation: "Answer every required part of the task and demonstrate the lesson skill.",
  },
  clarity: {
    label: "Clarity",
    explanation: "Make your ideas and writing easy to understand and follow.",
  },
  creativity: {
    label: "Creativity",
    explanation: "Use specific, original, and expressive choices where appropriate.",
  },
};
const EVALUATION_DIMENSION_TIE_ORDER = ["task_completion", "clarity", "creativity"];
const PERFORMANCE_DIMENSION_DETAILS = {
  task_completion: {
    descriptor: "How fully you answered the task",
    colorClass: "lp-performance-row--task",
  },
  clarity: {
    descriptor: "How easy your writing was to understand",
    colorClass: "lp-performance-row--clarity",
  },
  creativity: {
    descriptor: "How original and expressive your choices were",
    colorClass: "lp-performance-row--creativity",
  },
};

/*
 * Evaluation weights are intentionally not rendered on the lesson-start screen.
 */
function CompletedLessonNotice({
  week,
  lesson,
  title,
  primaryActionLabel,
  primaryAction,
  secondaryActionLabel,
  secondaryAction,
}) {
  const lessonTitle = String(title || "").trim();
  return (
    <main className="lp-completed-lesson-screen" aria-label="Lesson completed">
      <div className="lp-completed-lesson-card">
        <div className="lp-completed-lesson-badge">COMPLETED</div>
        <h1 className="lp-completed-lesson-title">Lesson Completed</h1>
        <p className="lp-completed-lesson-copy">
          You have already completed this lesson. Your saved submission, score, and coach feedback are available in Progress.
        </p>
        <div className="lp-completed-lesson-meta">
          Week {week}, Lesson {lesson}:{lessonTitle ? ` ${lessonTitle}` : ""}
        </div>
        <div className="lp-completed-lesson-actions">
          <button
            type="button"
            className="lp-btn lp-primary lp-completed-lesson-action lp-completed-lesson-action--primary"
            onClick={primaryAction}
          >
            {primaryActionLabel}
          </button>
          <button
            type="button"
            className="lp-btn lp-ghost lp-completed-lesson-action lp-completed-lesson-action--secondary"
            onClick={secondaryAction}
          >
            {secondaryActionLabel}
          </button>
        </div>
      </div>
    </main>
  );
}

function buildRevisedEmotionCurve(afterScore) {
  const score = Number(afterScore);
  if (!Number.isFinite(score) || MAGIC_BUILDING_EMOTION_CURVE.length === 0) return null;
  const nextValues = [...MAGIC_BUILDING_EMOTION_CURVE];
  [4, 5, 6].forEach((index) => {
    if (index < nextValues.length) nextValues[index] = score;
  });
  return nextValues;
}

function countWords(input) {
  const s = String(input || "").trim();
  if (!s) return 0;
  return s.split(/\s+/).filter(Boolean).length;
}

function normalizeComparableText(input) {
  return String(input || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function findNormalizedTextRange(source, needle) {
  const sourceText = String(source || "");
  const needleText = String(needle || "");
  if (!sourceText || !needleText) return null;

  const exactIndex = sourceText.indexOf(needleText);
  if (exactIndex >= 0) {
    return { start: exactIndex, end: exactIndex + needleText.length };
  }

  const normalize = (text) => {
    let normalized = "";
    const indexMap = [];
    Array.from(text).forEach((char, index) => {
      if (/\s/.test(char)) {
        if (normalized.endsWith(" ")) return;
        normalized += " ";
        indexMap.push(index);
        return;
      }
      normalized += char;
      indexMap.push(index);
    });
    return { normalized: normalized.trim().toLowerCase(), indexMap };
  };

  const sourceNormalized = normalize(sourceText);
  const needleNormalized = normalize(needleText);
  const startInNormalized = sourceNormalized.normalized.indexOf(needleNormalized.normalized);
  if (startInNormalized < 0) return null;

  const start = sourceNormalized.indexMap[startInNormalized] ?? 0;
  const endNormalized = startInNormalized + needleNormalized.normalized.length - 1;
  const end = (sourceNormalized.indexMap[endNormalized] ?? sourceText.length - 1) + 1;
  return { start, end };
}

function renderStoryWithHighlightedPassage(fullStory, originalPassage) {
  const story = String(fullStory || "");
  const passage = String(originalPassage || "");
  const range = findNormalizedTextRange(story, passage);
  if (!range) return story;

  return (
    <>
      {story.slice(0, range.start)}
      <span className="lp-curve-focus-passage">
        <span className="lp-curve-focus-label">Rewrite focus passage</span>
        {story.slice(range.start, range.end)}
      </span>
      {story.slice(range.end)}
    </>
  );
}

function extractHighlightedStoryPassage(fullStory, rewriteChallenge) {
  const story = String(fullStory || "");
  const fallback = String(rewriteChallenge?.original_passage || "").trim();
  const highlightStart = String(rewriteChallenge?.highlight_start || "").trim();
  const highlightEnd = String(rewriteChallenge?.highlight_end || "").trim();
  if (!story || !highlightStart || !highlightEnd) return fallback;

  const startRange = findNormalizedTextRange(story, highlightStart);
  if (!startRange) return fallback;

  const tail = story.slice(startRange.start);
  const endRange = findNormalizedTextRange(tail, highlightEnd);
  if (!endRange) return fallback;

  return story.slice(startRange.start, startRange.start + endRange.end).trim();
}

function normalizeSubmissionSections(lesson) {
  const sections = Array.isArray(lesson?.submission_sections)
    ? lesson.submission_sections
    : [];
  return sections
    .map((section) => ({
      key: String(section?.key || "").trim(),
      label: String(section?.label || section?.key || "").trim(),
      description: String(section?.description || "").trim(),
      placeholder: String(section?.placeholder || "").trim(),
      min_words: toFiniteNumber(section?.min_words),
      max_words: toFiniteNumber(section?.max_words),
      required: section?.required !== false && section?.read_only !== true,
      evaluation_role: String(section?.evaluation_role || "").trim(),
      word_count_constraint:
        section?.word_count_constraint && typeof section.word_count_constraint === "object"
          ? section.word_count_constraint
          : null,
      read_only: section?.read_only === true,
      content: String(section?.content || "").trim(),
    }))
    .filter((section) => section.key && section.label);
}

function composeStructuredSubmissionText(sections, values) {
  return (sections || [])
    .map((section) => {
      const value = String(values?.[section.key] || "").trim();
      if (section.read_only) {
        const readonlyValue = String(section.content || value || "").trim();
        return `${section.label}\n${readonlyValue}`;
      }
      return `${section.label}\n${value}`;
    })
    .join("\n\n")
    .trim();
}

function normalizeStructuredSubmission(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value)
      .map(([key, sectionValue]) => [String(key || "").trim(), String(sectionValue || "")])
      .filter(([key]) => key)
  );
}

function hasLeakedImplementationText(value) {
  const text = String(value || "");
  const leakedMarkers = [
    ["Files", "to", "inspect"].join(" "),
    ["Files", ":"].join(""),
    ["Evaluation", "strategy"].join(" "),
    ["Coach", "feedback"].join(" "),
    ["Tests", "to", "add"].join(" "),
    ["Validation", ":"].join(""),
    ["Output", ":"].join(""),
    ["Cod", "ex"].join(""),
    ["developer", "instructions"].join(" "),
    ["Implementation", "requirements"].join(" "),
    ["LLM", "feedback_eval", "prompt", "requirements"].join(" "),
  ];
  return leakedMarkers.some((marker) => text.toLowerCase().includes(marker.toLowerCase()));
}

function stripLeakedImplementationText(values, lesson) {
  if (!values || typeof values !== "object" || Array.isArray(values)) return {};
  const lessonId = String(lesson?.id || "").trim();
  if (lessonId !== "W4L1" && lessonId !== "W4L4") return values;
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => {
      const leaked = hasLeakedImplementationText(value);
      return [key, leaked ? "" : value];
    })
  );
}

function parseStructuredSubmissionText(sections, draftText) {
  const text = String(draftText || "");
  const labels = (sections || [])
    .map((section) => ({
      key: String(section?.key || "").trim(),
      label: String(section?.label || section?.key || "").trim(),
    }))
    .filter((section) => section.key && section.label);
  if (!labels.length || !text.trim()) return {};

  const parsed = {};
  labels.forEach((section, index) => {
    const startToken = section.label;
    const startIndex = text.toLowerCase().indexOf(startToken.toLowerCase());
    if (startIndex < 0) {
      parsed[section.key] = "";
      return;
    }
    const bodyStart = startIndex + startToken.length;
    let bodyEnd = text.length;
    labels.slice(index + 1).forEach((nextSection) => {
      const nextIndex = text.toLowerCase().indexOf(nextSection.label.toLowerCase(), bodyStart);
      if (nextIndex >= 0 && nextIndex < bodyEnd) bodyEnd = nextIndex;
    });
    parsed[section.key] = text.slice(bodyStart, bodyEnd).trim();
  });
  return parsed;
}

function buildSectionValidation(section, value, context = {}) {
  const sectionValue = section?.read_only ? section?.content || value : value;
  const words = countWords(sectionValue);
  const allValues = context?.allValues || {};
  const constraint = section?.word_count_constraint;
  const isMinimumRatio =
    constraint?.type === "minimum_ratio" && String(constraint?.relative_to || "").trim();
  const relativeKey = isMinimumRatio ? String(constraint.relative_to).trim() : "";
  const ratio = isMinimumRatio ? Number(constraint.ratio) : null;
  const relativeWords = countWords(allValues?.[relativeKey] || "");
  const ratioMin = isMinimumRatio && Number.isFinite(ratio) && ratio > 0 && relativeWords > 0
    ? Math.ceil(relativeWords * ratio)
    : null;
  const min = ratioMin ?? toFiniteNumber(section?.min_words);
  const max = ratioMin != null ? null : toFiniteNumber(section?.max_words);
  const required = section?.required !== false && !section?.read_only;
  const isEmpty = words === 0;
  const empty = required && isEmpty;
  const below = !section?.read_only && Number.isFinite(min) && min > 0 && words < min;
  const above = !section?.read_only && Number.isFinite(max) && max > 0 && words > max;
  const relativeLabel = isMinimumRatio
    ? String(context?.sectionLabels?.[relativeKey] || relativeKey).toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
    : "";
  const rangeLabel = isMinimumRatio
    ? Number.isFinite(min) && min > 0
      ? `at least ${min} words based on ${relativeLabel}`
      : "at least 40% longer than FAST VERSION"
    : Number.isFinite(min) && min > 0 && Number.isFinite(max) && max > 0
      ? `${min}-${max} words`
      : Number.isFinite(min) && min > 0
      ? `${min}+ words`
      : Number.isFinite(max) && max > 0
      ? `up to ${max} words`
      : "";
  const needed = below ? Math.max(0, min - words) : 0;
  const over = above ? Math.max(0, words - max) : 0;
  const status = empty ? "required" : !required && isEmpty ? "optional" : below ? "too_short" : above ? "too_long" : "ok";
  const ratioMessage = !empty && below && isMinimumRatio && Number.isFinite(ratio)
    ? `${section.label} must be at least ${Math.round((ratio - 1) * 100)}% longer than ${relativeLabel}. It currently needs at least ${min} words.`
    : "";
  const statusText = isEmpty && !section?.read_only && !required
    ? "Optional"
    : empty
    ? "Required"
    : ratioMessage
    ? ratioMessage
    : below
    ? `Too short, add ${needed}+ word${needed === 1 ? "" : "s"}`
    : above
    ? `Too long, cut ${over}+ word${over === 1 ? "" : "s"}`
    : "OK";
  const guidanceText =
    below && isMinimumRatio
      ? "Add sensory detail, pauses, thoughts, or micro-actions to slow the moment down."
      : "";
  return {
    ...section,
    words,
    min,
    max,
    below,
    above,
    needed,
    over,
    rangeLabel,
    status,
    statusText,
    guidanceText,
    ratioMessage,
    required,
    empty,
    valid: section?.read_only ? true : !required || (!empty && !below && !above),
  };
}

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeCoachFeedbackContract(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const pick = (value) => {
    if (Array.isArray(value)) {
      const first = value.find((item) => String(item || "").trim().length > 0);
      return first ? String(first).trim() : "";
    }
    return String(value || "").trim();
  };
  const explicitParagraphs = Array.isArray(src.paragraphs)
    ? src.paragraphs.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const paragraphs = explicitParagraphs.length
    ? explicitParagraphs
    : String(src.coach_feedback_paragraph || "")
        .split(/\n\s*\n/)
        .map((value) => value.trim())
        .filter(Boolean);
  const coachAvailable = typeof src.coach_available === "boolean"
    ? src.coach_available
    : paragraphs.length > 0 || Boolean(String(src.coach_feedback_paragraph || "").trim());
  return {
    paragraphs,
    outcome_mode: String(src.outcome_mode || "").trim(),
    coach_feedback_paragraph: pick(src.coach_feedback_paragraph),
    coach_available: coachAvailable,
    coach_source: String(src.coach_source || "").trim(),
    fallback_reason: String(src.fallback_reason || "").trim(),
    coach_notice: pick(src.coach_notice),
    hard_errors: Array.isArray(src.hard_errors) ? src.hard_errors.map((item) => String(item || "").trim()).filter(Boolean) : [],
    warnings: Array.isArray(src.warnings) ? src.warnings.map((item) => String(item || "").trim()).filter(Boolean) : [],
    api_call_count: Number.isFinite(Number(src.api_call_count)) ? Number(src.api_call_count) : null,
    what_worked: pick(src.what_worked),
    what_holding_back: pick(src.what_holding_back),
    next_step_message: pick(src.next_step_message),
    encouragement_message: pick(src.encouragement_message),
  };
}

function normalizeAnnotationList(items) {
  const out = [];
  const seen = new Set();
  if (!Array.isArray(items)) return out;
  items.forEach((raw) => {
    if (!raw || typeof raw !== "object") return;
    const start = Number(raw.start);
    const end = Number(raw.end);
    const text = String(raw.text || "");
    const signal = String(raw.signal || raw.label || "UNKNOWN");
    const source = String(
      raw.source ||
        raw.detector_used ||
        raw.detector ||
        raw.annotation_source ||
        "unknown"
    ).toLowerCase();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || text.length === 0) {
      return;
    }
    const key = `${start}:${end}:${signal}:${text}:${source}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ start, end, text, signal, source });
  });
  return out;
}

function normalizeWritingSignalAnnotationSets(data) {
  const writingSignals = data?.writing_signals && typeof data.writing_signals === "object"
    ? data.writing_signals
    : {};
  const basicRaw = Array.isArray(writingSignals.basic_annotations)
    ? writingSignals.basic_annotations
    : Array.isArray(data?.basic_annotations)
    ? data.basic_annotations
    : Array.isArray(data?.annotations)
    ? data.annotations
    : [];
  const enrichedRaw = Array.isArray(writingSignals.enriched_annotations)
    ? writingSignals.enriched_annotations
    : Array.isArray(data?.enriched_annotations)
    ? data.enriched_annotations
    : [];
  return {
    basic: normalizeAnnotationList(basicRaw),
    enriched: normalizeAnnotationList(enrichedRaw),
  };
}

function normalizeWritingSignalList(values) {
  if (!Array.isArray(values)) return [];
  const out = [];
  const seen = new Set();
  values.forEach((raw) => {
    const signalName = normalizeWritingSignalName(raw);
    if (!signalName || seen.has(signalName)) return;
    seen.add(signalName);
    out.push(signalName);
  });
  return out;
}

function normalizeLessonSignalMap(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};
  return Object.fromEntries(
    Object.entries(data)
      .map(([lessonId, signals]) => [normalizeWritingSignalName(lessonId), normalizeWritingSignalList(signals)])
      .filter(([lessonId, signals]) => lessonId && signals.length > 0)
  );
}

function normalizeLessonSignalDisplayMap(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};
  return Object.fromEntries(
    Object.entries(data)
      .map(([lessonId, cfg]) => {
        const src = cfg && typeof cfg === "object" && !Array.isArray(cfg) ? cfg : {};
        return [
          normalizeWritingSignalName(lessonId),
          {
            basic: normalizeWritingSignalList(src.basic),
            enriched: normalizeWritingSignalList(src.enriched),
          },
        ];
      })
      .filter(([lessonId, cfg]) => lessonId && (cfg.basic.length > 0 || cfg.enriched.length > 0))
  );
}

function normalizeWritingSignalDisplayConfig(data) {
  const writingSignals = data?.writing_signals && typeof data.writing_signals === "object"
    ? data.writing_signals
    : {};
  return {
    lessonSignalMap: normalizeLessonSignalMap(
      writingSignals.lesson_signal_map ?? data?.lesson_signal_map ?? {}
    ),
    lessonSignalDisplay: normalizeLessonSignalDisplayMap(
      writingSignals.lesson_signal_display ?? data?.lesson_signal_display ?? {}
    ),
  };
}

function normalizeWritingSignalName(value) {
  return String(value || "").trim().toUpperCase();
}

function getAnnotationSignalSet(annotations) {
  const names = new Set();
  (Array.isArray(annotations) ? annotations : []).forEach((ann) => {
    const signalName = normalizeWritingSignalName(ann?.signal || ann?.label);
    if (signalName) names.add(signalName);
  });
  return names;
}

function getLessonSignalDisplayGroups(lessonId, lessonSignalDisplay, lessonSignalMap) {
  const normalizedLessonId = normalizeWritingSignalName(lessonId);
  const configured = lessonSignalDisplay?.[normalizedLessonId];
  if (configured && (configured.basic.length > 0 || configured.enriched.length > 0)) {
    return {
      basic: [...configured.basic],
      enriched: [...configured.enriched],
    };
  }

  const fallback = Array.isArray(lessonSignalMap?.[normalizedLessonId])
    ? lessonSignalMap[normalizedLessonId]
    : [];
  return {
    basic: fallback.length > 0 ? [fallback[0]] : [],
    enriched: [...fallback],
  };
}

function dedupeWritingSignalAnnotations(annotations) {
  const out = [];
  const seen = new Set();
  (Array.isArray(annotations) ? annotations : []).forEach((ann) => {
    const key = [
      Number(ann?.start),
      Number(ann?.end),
      normalizeWritingSignalName(ann?.signal),
      String(ann?.text || "").trim(),
      String(ann?.source || "").trim().toLowerCase(),
    ].join(":");
    if (seen.has(key)) return;
    seen.add(key);
    out.push(ann);
  });
  return out;
}

function getLessonSignalAnnotationsForMode(
  lessonId,
  mode,
  annotations,
  lessonSignalDisplay,
  lessonSignalMap
) {
  if (mode === WRITING_SIGNAL_MODES.OFF) return [];
  const groups = getLessonSignalDisplayGroups(lessonId, lessonSignalDisplay, lessonSignalMap);
  const allowedSignals = new Set(
    mode === WRITING_SIGNAL_MODES.ENRICHED ? groups.enriched : groups.basic
  );
  if (allowedSignals.size === 0) return [];
  return (Array.isArray(annotations) ? annotations : []).filter((ann) =>
    allowedSignals.has(normalizeWritingSignalName(ann?.signal || ann?.label))
  );
}

function getWritingSignalModeAvailability(lessonId, annotations, lessonSignalDisplay, lessonSignalMap) {
  const groups = getLessonSignalDisplayGroups(lessonId, lessonSignalDisplay, lessonSignalMap);
  const detectedSignals = getAnnotationSignalSet(annotations);
  const basicSignals = new Set(groups.basic);
  const enrichedSignals = new Set(groups.enriched);
  let hasBasic = false;
  let hasEnriched = false;
  let hasEnrichedExtra = false;

  detectedSignals.forEach((signalName) => {
    if (basicSignals.has(signalName)) hasBasic = true;
    if (enrichedSignals.has(signalName)) {
      hasEnriched = true;
      if (!basicSignals.has(signalName)) hasEnrichedExtra = true;
    }
  });

  return { hasBasic, hasEnriched, hasEnrichedExtra };
}

function parseLessonLengthRule(lessonObj) {
  const src = lessonObj && typeof lessonObj === "object" ? lessonObj : {};
  const minCandidate = Number(src?.min_word_count);
  let minWords =
    Number.isFinite(minCandidate) && minCandidate > 0
      ? Math.round(minCandidate)
      : 0;
  let maxWords = null;

  const constraints = Array.isArray(src?.constraints) ? src.constraints : [];
  for (const raw of constraints) {
    const line = String(raw || "");
    // Supports common curriculum formats like "50-80 words" and "50–80 words".
    const rangeMatch = line.match(/(\d+)\s*[-–]\s*(\d+)\s*words?/i);
    if (rangeMatch) {
      const a = Number(rangeMatch[1]);
      const b = Number(rangeMatch[2]);
      if (Number.isFinite(a) && Number.isFinite(b)) {
        minWords = Math.min(a, b);
        maxWords = Math.max(a, b);
        break;
      }
    }
  }

  if (minWords > 0 && Number.isFinite(maxWords) && maxWords > minWords) {
    return {
      minWords,
      maxWords,
      displayLabel: `${minWords}\u2013${maxWords} words`,
    };
  }
  if (minWords > 0) {
    return {
      minWords,
      maxWords: null,
      displayLabel: `Minimum ${minWords} words`,
    };
  }
  return {
    minWords: 0,
    maxWords: null,
    displayLabel: "No word requirement",
  };
}

function getFeedbackStatus(
  score,
  passScore,
  attempt,
  maxAttempts,
  passed,
  displayFrame = null,
  options = {},
  lessonContext = null,
  lessonId = ""
) {
  const parsedScore = toFiniteNumber(score);
  const parsedPassScore = toFiniteNumber(passScore);
  const parsedAttempt = toFiniteNumber(attempt);
  const parsedMaxAttempts = toFiniteNumber(maxAttempts);
  const atMaxAttempts =
    parsedAttempt != null && parsedMaxAttempts != null
      ? parsedAttempt >= parsedMaxAttempts
      : parsedAttempt != null
      ? parsedAttempt >= 2
      : false;
  const frame = displayFrame && typeof displayFrame === "object" ? displayFrame : {};
  const passLabel = String(frame.pass_label || frame.status_label || "").trim();
  const retryLabel = String(frame.retry_label || frame.status_label || "").trim();
  const isCompletionReflection =
    /completion/i.test(passLabel) || /completion/i.test(retryLabel);
  const suppressCompletionLabels = Boolean(options?.suppressCompletionLabels);
  const performanceBand =
    parsedScore != null ? getPerformanceBand(parsedScore, lessonContext || {}, lessonId) : null;

  if (parsedScore != null && parsedPassScore != null) {
    if (isCompletionReflection && !suppressCompletionLabels) {
      return {
        heading: retryLabel || "Reflection saved",
        supportingLine: "Make the reflection specific to your writing, voice, growth, and next direction.",
        primaryCta: "next",
      };
    }
    if (suppressCompletionLabels) {
      return {
        heading: "Reflection saved",
        supportingLine: "Keep the reflection specific to your writing, voice, growth, and next direction.",
        primaryCta: "next",
      };
    }
    return {
      heading: performanceBand?.heading || passLabel || retryLabel || "You’re on track",
      supportingLine: performanceBand?.message || "Refine one key area and try again.",
      primaryCta: parsedScore < parsedPassScore ? (atMaxAttempts ? "next" : "retry") : "next",
    };
  }

  if (passed) {
    return {
      heading: passLabel || "You’re on track",
      supportingLine:
        "You met the standard. Now strengthen one area to improve further.",
      primaryCta: "next",
    };
  }

  return {
    heading: retryLabel || "Not there yet",
    supportingLine: "Refine one key area and try again.",
    primaryCta: atMaxAttempts ? "next" : "retry",
  };
}

export default function LessonPage() {
  const readOnly = isStudentView();
  const params = useParams();
  const rawStudentId = params.studentId || params.id || "SM01K4R9";
  const studentId = String(rawStudentId).toUpperCase();
  const { weekId, lessonId: lessonIdParam } = params;
  const navigate = useNavigate();
  const location = useLocation();
  const outletContext = useOutletContext();
  const setFocusModeActive =
    typeof outletContext?.setFocusModeActive === "function"
      ? outletContext.setFocusModeActive
      : null;
  const goToLessons =
    typeof outletContext?.goToLessons === "function" ? outletContext.goToLessons : null;
  const refreshLlmUsage =
    typeof outletContext?.refreshLlmUsage === "function"
      ? outletContext.refreshLlmUsage
      : null;
  const resetLlmUsage =
    typeof outletContext?.resetLlmUsage === "function"
      ? outletContext.resetLlmUsage
      : null;
  const onlineFromShell =
    typeof outletContext?.isOnline === "boolean" ? outletContext.isOnline : null;

  const inPortal = Boolean(params.id) && !params.studentId;
  const basePrefix = !inPortal
    ? `/student/${studentId}`
    : `/student/dashboard/${studentId}/lessons`;
  const progressPrefix = !inPortal
    ? `/student/${studentId}`
    : `/student/dashboard/${studentId}`;

  const weekNumber = parseInt(weekId || "1", 10);
  const lessonNumber = parseInt(lessonIdParam || "1", 10);
  const lessonId = canonicalLessonId(weekNumber, lessonNumber);

  // Read query params (for UX only; backend still controls gates/passed)
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const queryAttempt = parseInt(query.get("attempt") || "1", 10);
  const forceWeekOverview = query.get("week_overview") === "1";
  const devJump = query.get("dev_jump") === "1";

  // Attempt UI state: default from query, then backend can override later
  const [attempt, setAttempt] = useState(
    Number.isNaN(queryAttempt) ? 1 : queryAttempt
  );

  const [lessonContent, setLessonContent] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [weekIntroRequired, setWeekIntroRequired] = useState(false);
  const [weekIntroData, setWeekIntroData] = useState(null);
  const [startingWeek, setStartingWeek] = useState(false);
  const [startWeekError, setStartWeekError] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  const [lessonLoadError, setLessonLoadError] = useState("");
  const [lessonLoadDone, setLessonLoadDone] = useState(false);
  const [lessonResetSignal, setLessonResetSignal] = useState(0);
  const canonicalRefreshInFlightRef = useRef(false);
  const canonicalRefreshAtRef = useRef(0);

  // Structured lesson payload
  const [coachTips, setCoachTips] = useState([]);
  const [expandedPerformanceDimension, setExpandedPerformanceDimension] = useState(null);
  const [tooltipPerformanceDimension, setTooltipPerformanceDimension] = useState(null);

  // Draft + status
  const [text, setText] = useState("");
  const [structuredSubmission, setStructuredSubmission] = useState({});
  const [retryEditing, setRetryEditing] = useState(false);
  const retryEditingRef = useRef(false);
  const attemptOneDraftRef = useRef({ text: "", structuredSubmission: {} });
  const [status, setStatus] = useState("idle"); // idle|typing|saving_local|saved_local|saving_online|saved_online|offline|submitting|submitted|error
  const [dirty, setDirty] = useState(false);
  const [pasteNotice, setPasteNotice] = useState("");
  const textareaRef = useRef(null);
  const writingAreaRef = useRef(null);

  const submissionSections = useMemo(
    () => normalizeSubmissionSections(lessonContent),
    [lessonContent]
  );
  const hasStructuredSubmission = submissionSections.length > 0;
  const composedStructuredText = useMemo(
    () => composeStructuredSubmissionText(submissionSections, structuredSubmission),
    [submissionSections, structuredSubmission]
  );
  const effectiveDraftText = hasStructuredSubmission ? composedStructuredText : text;
  const wordCount = useMemo(() => countWords(effectiveDraftText), [effectiveDraftText]);
  const lessonTaskType = String(
    lessonContent?.task_type || lessonContent?.lesson_data?.task_type || ""
  ).trim();
  const lessonContentId = String(lessonContent?.id || lessonContent?.lesson_data?.id || "").trim();
  const rawLessonMode = lessonContent?.lesson_mode || lessonContent?.lesson_data?.lesson_mode || "";
  const lessonModeValue =
    typeof rawLessonMode === "string"
      ? rawLessonMode
      : String(rawLessonMode?.mode || "").trim();
  const isNarrativeCurveRewrite =
    lessonModeValue === "narrative_curve_rewrite" ||
    lessonTaskType === "structured_curve_rewrite";
  const rewriteChallenge = useMemo(
    () => lessonContent?.rewrite_challenge || {},
    [lessonContent]
  );
  const originalRewritePassage = useMemo(
    () =>
      lessonContentId === "W4L4"
        ? W4L4_SECTION_5_REWRITE_PASSAGE
        : extractHighlightedStoryPassage(
            lessonContent?.story_asset?.full_story || "",
            rewriteChallenge
          ),
    [lessonContentId, lessonContent?.story_asset?.full_story, rewriteChallenge]
  );
  const narrativeRewriteWordCount = countWords(text);
  const narrativeRewriteTooSimilar =
    isNarrativeCurveRewrite &&
    normalizeComparableText(text) === normalizeComparableText(originalRewritePassage);
  const isFinalRecognitionLesson =
    lessonTaskType === "final_reflection_submission";
  const shouldShowW4L4StoryCraftMap = lessonContentId === "W4L4" && !isNarrativeCurveRewrite;
  const displayCoachTips = useMemo(() => {
    if (lessonContentId !== "W2L2") return coachTips;
    return [
      "Focus for this lesson: Show what the character displays outwardly versus what they feel underneath.",
      "In this lesson, prioritize: Revealing contradiction through behavior, reactions, internal thought, habits, gestures, or dialogue.",
    ];
  }, [coachTips, lessonContentId]);
  const lessonTaskDecoder = String(
    lessonContent?.task_decoder || lessonContent?.lesson_data?.task_decoder || ""
  ).trim();
  useEffect(() => {
    if (!isNarrativeCurveRewrite) return;
    setStoryReadToEnd(false);
    setStoryNextPressed(false);
    window.requestAnimationFrame(() => {
      const el = curveStoryTextRef.current;
      if (el && el.scrollHeight <= el.clientHeight + 12) {
        setStoryReadToEnd(true);
      }
    });
  }, [isNarrativeCurveRewrite, lessonContentId, lessonContent?.story_asset?.full_story]);
  const structuredSectionStates = useMemo(
    () =>
      submissionSections.map((section) =>
        buildSectionValidation(section, structuredSubmission?.[section.key] || "", {
          allValues: structuredSubmission,
          sectionLabels: Object.fromEntries(submissionSections.map((item) => [item.key, item.label])),
        })
      ),
    [submissionSections, structuredSubmission]
  );
  const structuredTotalWordCount = useMemo(
    () => structuredSectionStates.reduce((sum, section) => sum + section.words, 0),
    [structuredSectionStates]
  );
  const structuredRequirementsText = useMemo(
    () =>
      structuredSectionStates
        .map((section) =>
          section.rangeLabel ? `${section.label} ${section.rangeLabel}` : section.label
        )
        .join(", "),
    [structuredSectionStates]
  );
  const structuredStatusText = useMemo(
    () =>
      structuredSectionStates
        .map((section) => {
          return `${section.label}: ${section.statusText}`;
        })
        .join(" · "),
    [structuredSectionStates]
  );
  const structuredBlockReason = useMemo(() => {
    const blocker = structuredSectionStates.find((section) => !section.valid);
    if (!blocker) return "";
    if (blocker.ratioMessage) return blocker.ratioMessage;
    if (blocker.empty) return `${blocker.label} is required.`;
    if (blocker.below) {
      const base = `${blocker.label} needs ${blocker.needed} more word${blocker.needed === 1 ? "" : "s"}.`;
      return blocker.guidanceText ? `${base} ${blocker.guidanceText}` : base;
    }
    return `${blocker.label} is ${blocker.over} word${blocker.over === 1 ? "" : "s"} over the limit.`;
  }, [structuredSectionStates]);
  const structuredSectionsValid =
    !hasStructuredSubmission || structuredSectionStates.every((section) => section.valid);

  useEffect(() => {
    if (!hasStructuredSubmission) return;
    if (text === composedStructuredText) return;
    setText(composedStructuredText);
  }, [hasStructuredSubmission, text, composedStructuredText]);

  // Draft key (per student + lesson)
  const draftKey = useMemo(
    () => `${readOnly ? "student_view:" + getStudentView().token + ":" : ""}draft:${studentId}:W${weekNumber}:L${lessonNumber}`,
    [studentId, weekNumber, lessonNumber, readOnly]
  );
  const structuredDraftKey = useMemo(
    () => `${readOnly ? "student_view:" + getStudentView().token + ":" : ""}structured_draft:${studentId}:W${weekNumber}:L${lessonNumber}`,
    [studentId, weekNumber, lessonNumber, readOnly]
  );
  const submissionRecoveryKey = useMemo(
    () => buildSubmissionRecoveryKey(studentId, weekNumber, lessonNumber),
    [studentId, weekNumber, lessonNumber]
  );
  const submissionRecoveryVersionKey = useMemo(
    () => `${submissionRecoveryKey}:feedback_version`,
    [submissionRecoveryKey]
  );
  const legacySubmissionRecoveryKey = useMemo(
    () => `pending_submission:${studentId}:W${weekNumber}:L${lessonNumber}`,
    [studentId, weekNumber, lessonNumber]
  );
  const clearSubmissionRecoveryKey = useCallback(() => {
    try {
      getSubmissionRecoveryKeys(studentId, weekNumber, lessonNumber).forEach((key) => {
        localStorage.removeItem(key);
      });
      if (submissionRecoveryVersionKey) {
        localStorage.removeItem(submissionRecoveryVersionKey);
      }
      localStorage.removeItem(`${legacySubmissionRecoveryKey}:feedback_version`);
    } catch {
      // non-blocking
    }
  }, [
    legacySubmissionRecoveryKey,
    studentId,
    submissionRecoveryVersionKey,
    weekNumber,
    lessonNumber,
  ]);

  const clearLessonCacheArtifacts = useCallback(
    (override = {}) => {
      const resetStudentId = String(override?.studentId || studentId || "").trim();
      const resetWeekNumber = Number(override?.week || weekNumber);
      const resetLessonNumber = Number(override?.lesson || lessonNumber);
      const resetUpperStudentId = resetStudentId.toUpperCase();
      const lessonToken = `W${resetWeekNumber}:L${resetLessonNumber}`;
      const draftKeyCurrent = `draft:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`;
      const structuredDraftKeyCurrent = `structured_draft:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`;
      const currentSubmissionRecoveryKey = buildSubmissionRecoveryKey(
        resetStudentId,
        resetWeekNumber,
        resetLessonNumber
      );
      const legacySubmissionRecoveryKeyCurrent = `pending_submission:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`;
      const lessonKeys = [
        ...getSubmissionRecoveryKeys(resetStudentId, resetWeekNumber, resetLessonNumber),
        `${currentSubmissionRecoveryKey}:feedback_version`,
        `${legacySubmissionRecoveryKeyCurrent}:feedback_version`,
        `pending_submission:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}:feedback_version`,
        `pending_submission:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}:error`,
        `pending_submission:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}:result`,
        `lesson_result:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`,
        `result_cache:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`,
        `feedback_cache:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`,
        `latestSubmissionId:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`,
        `latestSubmission:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`,
        `latest_submission_id:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`,
        `lastSubmissionId:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`,
        `lastSubmission:${resetStudentId}:W${resetWeekNumber}:L${resetLessonNumber}`,
      ];
      try {
        localStorage.removeItem(draftKeyCurrent);
        localStorage.removeItem(structuredDraftKeyCurrent);
        lessonKeys.forEach((key) => localStorage.removeItem(key));
        Object.keys(localStorage).forEach((key) => {
          if (key.includes(lessonToken) && key.includes(resetUpperStudentId)) {
            localStorage.removeItem(key);
          }
        });
      } catch {
        // non-blocking
      }
      try {
        Object.keys(sessionStorage).forEach((key) => {
          if (
            lessonKeys.includes(key) ||
            key === draftKeyCurrent ||
            key === structuredDraftKeyCurrent ||
            (key.includes(lessonToken) && key.includes(resetUpperStudentId))
          ) {
            sessionStorage.removeItem(key);
          }
        });
      } catch {
        // non-blocking
      }
    },
    [lessonNumber, studentId, weekNumber]
  );

  // prevent autosave effects from firing immediately on initial load
  const didHydrateRef = useRef(false);

  // Change this if your backend draft endpoint differs:
  const DRAFT_SAVE_URL = `${API}/api/save_draft`;

  const [feedback, setFeedback] = useState(null);
  const [feedbackPhase, setFeedbackPhase] = useState("idle"); // idle|loading|celebrate|show
  const [feedbackSourceEndpoint, setFeedbackSourceEndpoint] = useState("");
  const feedbackStreamRef = useRef(null);
  const applyCompletedFeedbackRef = useRef(null);
  const [confirmedProgress, setConfirmedProgress] = useState(10);
  const [displayedProgress, setDisplayedProgress] = useState(10);
  const [displayedStage, setDisplayedStage] = useState("Submission received");
  const [displayedSubtitle, setDisplayedSubtitle] = useState("Your writing has been submitted successfully.");
  const [pendingFeedbackResult, setPendingFeedbackResult] = useState(null);
  const [displayedStatus, setDisplayedStatus] = useState("idle");
  const [feedbackSlow, setFeedbackSlow] = useState(false);
  const feedbackProgressStartedAtRef = useRef(0);
  const feedbackProgressFinalizeTimerRef = useRef(null);
  const feedbackProgressHoldTimerRef = useRef(null);
  const feedbackProgressAnimationRef = useRef(null);
  const feedbackProgressStageTimersRef = useRef([]);
  const feedbackProgressReadyRef = useRef(false);
  const feedbackProgressScrollRef = useRef(null);
  const feedbackProgressScrollRequestedRef = useRef(false);
  const feedbackSseSeqRef = useRef(0);
  const [writingSignalsReviewed, setWritingSignalsReviewed] = useState(false);
  const [storyCurveReviewed, setStoryCurveReviewed] = useState(false);
  const [emotionCurveView, setEmotionCurveView] = useState("before");
  const [emotionCurveAnimationKey, setEmotionCurveAnimationKey] = useState(0);
  const [w4l4ChartIntroSeen, setW4l4ChartIntroSeen] = useState(false);
  const [w4l4ChartIntroPhase, setW4l4ChartIntroPhase] = useState("complete");
  const [w4l4VisiblePointCount, setW4l4VisiblePointCount] = useState(W4L4_CHART_POINT_COUNT);
  const [storyReadToEnd, setStoryReadToEnd] = useState(false);
  const [storyNextPressed, setStoryNextPressed] = useState(false);
  const [, setGroundedFeedback] = useState(null);
  const [, setFeedbackMeta] = useState({
    score: null,
    passScore: null,
    status: null,
    attempt: null,
    maxAttempts: null,
    rubric: null,
    evidence: null,
  });
  const [coach, setCoach] = useState(null);
  const [microLesson, setMicroLesson] = useState(null);
  const [submitGuardMessage, setSubmitGuardMessage] = useState("");
  const [showBelowMinConfirm, setShowBelowMinConfirm] = useState(false);
  const [showImproveDraftConfirm, setShowImproveDraftConfirm] = useState(false);
  const belowMinPrimaryRef = useRef(null);
  const curveStoryTextRef = useRef(null);
  const curveChartRef = useRef(null);
  const w4l4ChartIntroTimerRef = useRef([]);
  const w4l4StartWritingPreloadedRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const feedbackRequestRef = useRef(0);
  const w1l1StaleRefreshRef = useRef(0);
  const pollTimerRef = useRef(null);
  const [isOnline, setIsOnline] = useState(() =>
    onlineFromShell == null ? navigator.onLine : onlineFromShell
  );
  const [activeSubmissionId, setActiveSubmissionId] = useState(null);
  const [processingFailed, setProcessingFailed] = useState(false);

  const clearProgressTimers = useCallback(() => {
    if (feedbackProgressFinalizeTimerRef.current) {
      window.clearTimeout(feedbackProgressFinalizeTimerRef.current);
      feedbackProgressFinalizeTimerRef.current = null;
    }
    if (feedbackProgressHoldTimerRef.current) {
      window.clearTimeout(feedbackProgressHoldTimerRef.current);
      feedbackProgressHoldTimerRef.current = null;
    }
    if (feedbackProgressAnimationRef.current) {
      window.clearInterval(feedbackProgressAnimationRef.current);
      feedbackProgressAnimationRef.current = null;
    }
    feedbackProgressStageTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    feedbackProgressStageTimersRef.current = [];
  }, []);

  const resetProgressPanelState = useCallback(() => {
    clearProgressTimers();
    feedbackProgressStartedAtRef.current = 0;
    feedbackProgressReadyRef.current = false;
    setPendingFeedbackResult(null);
    setConfirmedProgress(10);
    setDisplayedProgress(10);
    setDisplayedStage("Submission received");
    setDisplayedSubtitle("Your writing has been submitted successfully.");
    setDisplayedStatus("idle");
  }, [clearProgressTimers]);

  const stopFeedbackProgressStream = useCallback(() => {
    if (feedbackStreamRef.current) {
      try {
        feedbackStreamRef.current.close();
      } catch {
        // non-blocking
      }
      feedbackStreamRef.current = null;
    }
  }, []);

  const setFeedbackProgressFromEvent = useCallback((eventData) => {
    const rawProgress = Number(eventData?.progress);
    const statusValue = String(eventData?.status || "processing");
    const isTerminalSuccess = statusValue === "completed" || statusValue === "completed_with_fallback";
    const progressValue = isTerminalSuccess
      ? 100
      : Number.isFinite(rawProgress)
      ? Math.max(0, Math.min(statusValue === "failed" || statusValue === "error" ? 99 : 100, Math.round(rawProgress)))
      : 10;
    if (statusValue === "failed" || statusValue === "error") {
      setDisplayedProgress((current) => Math.min(99, current));
    } else {
      setDisplayedProgress((current) => Math.max(current, progressValue));
    }
    setConfirmedProgress((current) => isTerminalSuccess ? 100 : Math.max(current, progressValue));
    setDisplayedStage(String(eventData?.stage || "Feedback processing"));
    setDisplayedSubtitle(String(eventData?.subtitle || ""));
    setDisplayedStatus(statusValue);
    if (statusValue === "completed" || statusValue === "completed_with_fallback") {
      setPendingFeedbackResult(eventData?.result || null);
    }
  }, []);

  useEffect(
    () => () => {
      stopFeedbackProgressStream();
      clearProgressTimers();
    },
    [clearProgressTimers, stopFeedbackProgressStream]
  );

  useEffect(() => {
    if (lessonContentId !== "W4L4") {
      w4l4ChartIntroTimerRef.current.forEach((timerId) => window.clearTimeout(timerId));
      w4l4ChartIntroTimerRef.current = [];
      setW4l4ChartIntroSeen(false);
      setW4l4ChartIntroPhase("complete");
      setW4l4VisiblePointCount(W4L4_CHART_POINT_COUNT);
      return;
    }

    let seen = false;
    try {
      seen = localStorage.getItem(W4L4_CHART_INTRO_SEEN_KEY) === "1";
    } catch (error) {
      seen = false;
    }

    setW4l4ChartIntroSeen(seen);
    setW4l4ChartIntroPhase(seen ? "complete" : "idle_waiting_for_view");
    setW4l4VisiblePointCount(seen ? W4L4_CHART_POINT_COUNT : 0);
  }, [lessonContentId]);

  useEffect(() => {
    return () => {
      w4l4ChartIntroTimerRef.current.forEach((timerId) => window.clearTimeout(timerId));
      w4l4ChartIntroTimerRef.current = [];
    };
  }, [clearSubmissionRecoveryKey]);

  useEffect(() => {
    setWritingSignalsReviewed(false);
    setStoryCurveReviewed(false);
  }, [lessonContentId, attempt, feedback?.submitted_at, feedbackPhase]);

  useEffect(() => {
    if (lessonContentId !== "W4L4") return undefined;
    const handleW4L4ChartIntroReset = () => {
      w4l4ChartIntroTimerRef.current.forEach((timerId) => window.clearTimeout(timerId));
      w4l4ChartIntroTimerRef.current = [];
      w4l4StartWritingPreloadedRef.current = false;
      setW4l4ChartIntroSeen(false);
      setW4l4ChartIntroPhase("idle_waiting_for_view");
      setW4l4VisiblePointCount(0);
      setStoryReadToEnd(false);
      setStoryNextPressed(false);
    };
    window.addEventListener("writeflow:w4l4-chart-intro-reset", handleW4L4ChartIntroReset);
    return () => {
      window.removeEventListener("writeflow:w4l4-chart-intro-reset", handleW4L4ChartIntroReset);
    };
  }, [lessonContentId]);

  useEffect(() => {
    const hasEmotionFeedback =
      feedbackPhase === "show" &&
      feedback?.story_curve_update &&
      typeof feedback.story_curve_update === "object" &&
      feedback.story_curve_update.target_curve === "emotion";

    if (
      lessonContentId !== "W4L4" ||
      !storyNextPressed ||
      hasEmotionFeedback ||
      w4l4ChartIntroSeen ||
      w4l4ChartIntroPhase !== "idle_waiting_for_view"
    ) {
      return;
    }

    const node = curveChartRef.current;
    if (!node || typeof IntersectionObserver !== "function") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.8)) {
          return;
        }

        w4l4ChartIntroTimerRef.current.forEach((timerId) => window.clearTimeout(timerId));
        w4l4ChartIntroTimerRef.current = [];
        setW4l4VisiblePointCount(0);
        setW4l4ChartIntroPhase(W4L4_INTRO_CARD_PHASE);
        observer.disconnect();
      },
      { threshold: [0, 0.8, 1] }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    feedback,
    feedbackPhase,
    lessonContentId,
    storyNextPressed,
    w4l4ChartIntroSeen,
    w4l4ChartIntroPhase,
  ]);

  useEffect(() => {
    w4l4ChartIntroTimerRef.current.forEach((timerId) => window.clearTimeout(timerId));
    w4l4ChartIntroTimerRef.current = [];
    if (lessonContentId !== "W4L4" || w4l4ChartIntroPhase !== "intro_step_1_animating") {
      return undefined;
    }

    const reducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reducedMotion) {
      setW4l4VisiblePointCount(W4L4_CHART_POINT_COUNT);
      setW4l4ChartIntroPhase("intro_step_1_done");
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setW4l4VisiblePointCount((current) => {
        const next = Math.min(W4L4_CHART_POINT_COUNT, current + 1);
        if (next >= W4L4_CHART_POINT_COUNT) {
          window.clearInterval(intervalId);
          setW4l4ChartIntroPhase("intro_step_1_done");
        }
        return next;
      });
    }, 120);
    w4l4ChartIntroTimerRef.current.push(intervalId);

    return () => {
      window.clearInterval(intervalId);
      w4l4ChartIntroTimerRef.current = w4l4ChartIntroTimerRef.current.filter(
        (timerId) => timerId !== intervalId
      );
    };
  }, [lessonContentId, w4l4ChartIntroPhase]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && lessonContentId === "W4L4") {
      console.log("[W4L4 intro]", {
        seen: w4l4ChartIntroSeen,
        phase: w4l4ChartIntroPhase,
        visiblePointCount: w4l4VisiblePointCount,
      });
    }
  }, [lessonContentId, w4l4ChartIntroSeen, w4l4ChartIntroPhase, w4l4VisiblePointCount]);

  // NER highlight spans from backend
  const [annotations, setAnnotations] = useState([]);
  const [, setWritingSignalAnnotationSets] = useState({
    basic: [],
    enriched: [],
  });
  const [writingSignalDisplayConfig, setWritingSignalDisplayConfig] = useState({
    lessonSignalDisplay: {},
    lessonSignalMap: {},
  });
  const [writingSignalMode, setWritingSignalMode] = useState(WRITING_SIGNAL_MODES.OFF);

  // Mentor outputs from backend
  const [mentorTip, setMentorTip] = useState(null);
  const [mentorPlan, setMentorPlan] = useState(null);
  const [mentorError, setMentorError] = useState(null);

  const [nextLesson, setNextLesson] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [lessonClosed, setLessonClosed] = useState(false);

  useEffect(() => {
    if (!loadingFeedback) return undefined;

    clearProgressTimers();
    feedbackProgressReadyRef.current = false;
    feedbackProgressStartedAtRef.current = Date.now();
    setFeedbackSlow(false);
    setDisplayedProgress(10);
    setDisplayedStage("Submission received");
    setDisplayedSubtitle("Your writing has been submitted successfully.");
    setDisplayedStatus("processing");

    return () => {
      feedbackProgressStageTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      feedbackProgressStageTimersRef.current = [];
    };
  }, [clearProgressTimers, loadingFeedback]);

  useEffect(() => {
    if (!loadingFeedback || !feedbackProgressScrollRequestedRef.current) return undefined;

    feedbackProgressScrollRequestedRef.current = false;
    const frameId = window.requestAnimationFrame(() => {
      scrollFeedbackProgressIntoView(feedbackProgressScrollRef);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [loadingFeedback]);

  useEffect(() => {
    if (!loadingFeedback || displayedStatus !== "processing") return undefined;
    if (confirmedProgress >= 90 || feedbackProgressCap(confirmedProgress) <= displayedProgress) {
      return undefined;
    }

    feedbackProgressAnimationRef.current = window.setInterval(() => {
      setDisplayedProgress((current) => advanceFeedbackProgress(current, confirmedProgress));
    }, 250);

    return () => {
      if (feedbackProgressAnimationRef.current) {
        window.clearInterval(feedbackProgressAnimationRef.current);
        feedbackProgressAnimationRef.current = null;
      }
    };
  }, [confirmedProgress, displayedProgress, displayedStatus, loadingFeedback]);

  useEffect(() => {
    if (!loadingFeedback) return undefined;
    const slowTimer = window.setTimeout(() => {
      setFeedbackSlow(true);
    }, FEEDBACK_PROGRESS_SLOW_WARNING_MS);
    const maxWaitTimer = window.setTimeout(() => {
      stopFeedbackProgressStream();
      stopSubmissionPolling();
      setLoadingFeedback(false);
      setFeedbackPhase("idle");
      setProcessingFailed(true);
      setStatus("error");
      setSubmitGuardMessage("Feedback timed out. Please try again.");
    }, FEEDBACK_PROGRESS_MAX_WAIT_MS);
    return () => {
      window.clearTimeout(slowTimer);
      window.clearTimeout(maxWaitTimer);
    };
  }, [loadingFeedback, stopFeedbackProgressStream]);

  useEffect(() => {
    if (!loadingFeedback || !pendingFeedbackResult) return undefined;
    if (displayedProgress < 90) return undefined;
    if (feedbackProgressReadyRef.current) return undefined;

    const startedAt = feedbackProgressStartedAtRef.current || Date.now();
    const elapsed = Date.now() - startedAt;
    const waitMs = Math.max(0, FEEDBACK_PROGRESS_MIN_VISIBLE_MS - elapsed);
    feedbackProgressReadyRef.current = true;

    feedbackProgressFinalizeTimerRef.current = window.setTimeout(() => {
      feedbackProgressFinalizeTimerRef.current = null;
      setDisplayedProgress(100);
      setDisplayedStage("Feedback ready");
      setDisplayedSubtitle("Your feedback is ready.");
      setDisplayedStatus("completed");
      feedbackProgressHoldTimerRef.current = window.setTimeout(() => {
        feedbackProgressHoldTimerRef.current = null;
        applyCompletedFeedbackRef.current?.(
          pendingFeedbackResult,
          String(feedbackSourceEndpoint || "")
        );
      }, FEEDBACK_PROGRESS_POST_COMPLETE_HOLD_MS);
    }, waitMs);

    return () => {
      if (feedbackProgressFinalizeTimerRef.current) {
        window.clearTimeout(feedbackProgressFinalizeTimerRef.current);
        feedbackProgressFinalizeTimerRef.current = null;
      }
      if (feedbackProgressHoldTimerRef.current) {
        window.clearTimeout(feedbackProgressHoldTimerRef.current);
        feedbackProgressHoldTimerRef.current = null;
      }
      feedbackProgressReadyRef.current = false;
    };
  }, [displayedProgress, feedbackSourceEndpoint, loadingFeedback, pendingFeedbackResult]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[feedback-progress]", {
        confirmedProgress,
        displayedProgress,
        displayedStage,
        pendingFeedbackResult: Boolean(pendingFeedbackResult),
      });
    }
  }, [confirmedProgress, displayedProgress, displayedStage, pendingFeedbackResult]);

  const [rubricScore, setRubricScore] = useState(null);
  const [rubricDimensionScores, setRubricDimensionScores] = useState(null);
  const [attemptsAllowed, setAttemptsAllowed] = useState(null);

  // backend truth for pass/fail
  const [passed, setPassed] = useState(false);

  // Backend-driven toggle (from /feedback response)
  const [gatesEnforced, setGatesEnforced] = useState(true);

  // attempt2 flags from backend
  const [allowRepeat, setAllowRepeat] = useState(false);
  const [allowOptionalImprovementAttempt, setAllowOptionalImprovementAttempt] =
    useState(false);
  const [improvementAttemptLabel, setImprovementAttemptLabel] =
    useState("Improve Draft");

  // Deterministic learning state from backend
  const [learningState, setLearningState] = useState(null); // PASS|RETRY|INTERVENTION|RETRY_LIMIT
  const [courseCompleted, setCourseCompleted] = useState(false);
  const [courseCompleteView, setCourseCompleteView] = useState(false);
  const [, setShowGraduation] = useState(false);
  const [requiresWeekReflection, setRequiresWeekReflection] = useState(false);
  const [pendingReflectionWeek, setPendingReflectionWeek] = useState(null);

  // Gate: require watching lesson video to the end
  const [videoWatched, setVideoWatched] = useState(false);
  const [videoGatesEnforced, setVideoGatesEnforced] = useState(true);
  const [adminLessonTestModeEnabled, setAdminLessonTestModeEnabled] = useState(null);
  const [nerHighlightsEnabled, setNerHighlightsEnabled] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const mentorSignalsAutoTriggeredRef = useRef(false);

  const focusModeSessionKey = useMemo(
    () => `writeflow_focus_mode_${studentId}`,
    [studentId]
  );
  const isAdminLessonTestMode = useMemo(
    () =>
      adminLessonTestModeEnabled === true &&
      isAdminUser({
        student_id: studentId,
        role: localStorage.getItem("role"),
        is_admin: localStorage.getItem("is_admin") === "1",
      }),
    [adminLessonTestModeEnabled, studentId]
  );
  const canonicalAuthUser = useMemo(() => readStoredAuthUser(), []);

  // ========================================
  // Nice UX labels for status
  // ========================================
  const statusLabelMap = {
    idle: "Idle",
    typing: "Typing…",
    saving_local: "Saving (local)…",
    saved_local: "Saved (local)",
    saving_online: "Syncing…",
    saved_online: "Saved (synced)",
    offline: "Saved (local) • Not synced",
    submitting: "Submitting…",
    submitted: "Submitted",
    error: "Error",
  };

  useEffect(() => {
    if (onlineFromShell == null) return;
    setIsOnline(onlineFromShell);
  }, [onlineFromShell]);

  useEffect(() => {
    if (onlineFromShell != null) return undefined;

    function handleOnline() {
      setIsOnline(true);
    }
    function handleOffline() {
      setIsOnline(false);
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [onlineFromShell]);

  // ========================================
  // Render structured coach tips (no markdown)
  // ========================================
  function renderCoachTipsList(tips) {
    if ((!Array.isArray(tips) || tips.length === 0) && !lessonTaskDecoder) return null;

    return (
      <div className="lp-box lp-coach">
        <div className="lp-box-title">Coach Tips</div>
        {lessonTaskDecoder ? (
          <div className="lp-coach-task-decoder">
            <strong>Task decoder:</strong> {lessonTaskDecoder}
          </div>
        ) : null}
        <div className="lp-box-text">Keep these points in mind as you write.</div>
        {Array.isArray(tips) && tips.length > 0 ? (
          <ul className="lp-list-reset">
            {tips.map((t, i) => (
              <li key={i} className="lp-li">
                {capitalizeFirstCharacter(
                  String(t).replace(/^(Focus for this week|In this lesson, prioritize):\s*/i, "")
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  function renderLessonStartGuidance() {
    return (
      <p className="lp-lesson-start-guidance">
        Your response will be considered for how fully it answers the task, how clearly it communicates, and how effectively it uses creative choices.
      </p>
    );
  }

  // Rubric dimensions remain calculated and stored for tutor/admin consumers; student rendering is intentionally suppressed.
  // eslint-disable-next-line no-unused-vars
  function renderPerformanceCard() {
    if (feedbackPhase !== "show" || !feedback) return null;
    const scoresAvailable = EVALUATION_DIMENSION_TIE_ORDER.every((key) => {
      const score = Number(rubricDimensionScores?.[key]);
      return Number.isFinite(score) && score >= 0 && score <= 10;
    });
    if (!scoresAvailable) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("RUBRIC_PERFORMANCE_RENDER_WARN: missing authoritative dimension scores", {
          lessonId: lessonContentId,
          attempt,
        });
      }
      return null;
    }

    const rows = EVALUATION_DIMENSION_TIE_ORDER.map((key) => {
      const score = Number(rubricDimensionScores[key]);
      const percentage = Math.max(0, Math.min(100, (score / 10) * 100));
      const roundedPercentage = Math.round((percentage + Number.EPSILON) * 10) / 10;
      return {
        key,
        ...EVALUATION_DIMENSIONS[key],
        ...PERFORMANCE_DIMENSION_DETAILS[key],
        score,
        percentage: roundedPercentage,
        percentageText: `${Number.isInteger(roundedPercentage) ? roundedPercentage : roundedPercentage.toFixed(1)}%`,
      };
    });

    return (
      <section className="lp-performance-card" aria-labelledby="performance-card-title">
        <div className="lp-performance-card-head">
          <div>
            <h3 id="performance-card-title">How you performed</h3>
            <p>Your performance across the three main scoring areas.</p>
          </div>
        </div>
        <div className="lp-performance-rows">
          {rows.map((row) => (
            <div key={row.key} className={`lp-performance-row ${row.colorClass}`.trim()}>
              <button
                type="button"
                className={`lp-performance-row-button ${
                  expandedPerformanceDimension === row.key ? "is-expanded" : ""
                }`.trim()}
                aria-expanded={expandedPerformanceDimension === row.key}
                aria-controls={`performance-detail-${row.key}`}
                aria-label={`${row.label} performance: ${row.percentageText.replace("%", " percent")}`}
                aria-describedby={
                  tooltipPerformanceDimension === row.key
                    ? `performance-tooltip-${row.key}`
                    : undefined
                }
                onMouseEnter={() => setTooltipPerformanceDimension(row.key)}
                onMouseLeave={() => setTooltipPerformanceDimension(null)}
                onFocus={() => setTooltipPerformanceDimension(row.key)}
                onBlur={() => setTooltipPerformanceDimension(null)}
                onClick={() => {
                  setTooltipPerformanceDimension(null);
                  setExpandedPerformanceDimension((current) =>
                    current === row.key ? null : row.key
                  );
                }}
              >
                <span className="lp-performance-row-label">
                  <strong id={`performance-label-${row.key}`}>{row.label}</strong>
                  <span>{row.descriptor}</span>
                </span>
                <span className="lp-performance-row-percentage">{row.percentageText}</span>
                <span
                  className="lp-performance-row-track"
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={row.percentage}
                  aria-label={`${row.label} performance: ${row.percentageText.replace("%", " percent")}`}
                >
                  <span
                    className="lp-performance-row-fill"
                    style={{ "--performance-width": `${row.percentage}%` }}
                  />
                </span>
                <span className="lp-performance-row-affordance" aria-hidden="true">
                  {expandedPerformanceDimension === row.key ? "⌃" : "⌄"}
                </span>
                {tooltipPerformanceDimension === row.key ? (
                  <span
                    id={`performance-tooltip-${row.key}`}
                    className="lp-performance-tooltip"
                    role="tooltip"
                  >
                    {row.explanation}
                  </span>
                ) : null}
              </button>
              {expandedPerformanceDimension === row.key ? (
                <div
                  id={`performance-detail-${row.key}`}
                  className="lp-performance-detail"
                  role="region"
                  aria-labelledby={`performance-label-${row.key}`}
                >
                  {row.explanation}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </section>
    );
  }

  // ========================================
  // Mentor box (only if mentor_tip exists)
  // ========================================
  function renderMentorBox() {
    const isFinalMentorSummary =
      mentorTip &&
      typeof mentorTip === "object" &&
      !Array.isArray(mentorTip) &&
      (mentorTip.title || mentorTip.message);
    const isStructuredMentorTip =
      mentorTip &&
      typeof mentorTip === "object" &&
      !Array.isArray(mentorTip) &&
      (mentorTip.week_focus ||
        mentorTip.strength_note ||
        mentorTip.growth_note ||
        mentorTip.revision_move);

    if (!mentorTip) return null;
    if (!isStructuredMentorTip && String(mentorTip).trim().length === 0)
      return null;

    const primaryFocus = mentorPlan?.primary_focus
      ? String(mentorPlan.primary_focus).replace("_", " ")
      : null;

    if (isFinalMentorSummary) {
      return (
        <div className="lp-box lp-mentor" data-tour="lesson-mentor">
          <div className="lp-guidance-head">
            <h3 className="lp-guidance-title">
              Mentor guidance based on Shaanzae’s teaching
            </h3>
            <p className="lp-guidance-copy">
              Keep this in mind as you grow from lesson to lesson.
            </p>
          </div>
          {primaryFocus ? (
            <div className="lp-guidance-focus">
              <strong>Current focus:</strong> {primaryFocus}
            </div>
          ) : null}
          <div className="lp-guidance-focus">
            <strong>Week focus:</strong> {mentorTip.title || "Course progress"}
          </div>
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
            {mentorTip.message || "You completed the course. Keep writing regularly to maintain your momentum."}
          </div>
          {mentorError ? (
            <div style={{ marginTop: 10, fontSize: 12, color: "tomato" }}>
              Mentor error: {mentorError}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="lp-box lp-mentor" data-tour="lesson-mentor">
        <div className="lp-guidance-head">
          <h3 className="lp-guidance-title">
            Mentor guidance based on Shaanzae’s teaching
          </h3>
          <p className="lp-guidance-copy">
            Keep this in mind as you grow from lesson to lesson.
          </p>
        </div>

        {isStructuredMentorTip ? (
          <div style={{ lineHeight: 1.45 }}>
            {primaryFocus ? (
              <div className="lp-guidance-focus">
                <strong>Current focus:</strong> {primaryFocus}
              </div>
            ) : null}
            <div>
              <strong>Week focus:</strong> {mentorTip.week_focus}
            </div>
            <div>
              <strong>Strength:</strong> {mentorTip.strength_note}
            </div>
            <div>
              <strong>Growth:</strong> {mentorTip.growth_note}
            </div>
            <div>
              <strong>Revision move:</strong> {mentorTip.revision_move}
            </div>
          </div>
        ) : (
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
            {mentorTip}
          </div>
        )}

        {/* Dev visibility */}
        {mentorError ? (
          <div style={{ marginTop: 10, fontSize: 12, color: "tomato" }}>
            Mentor error: {mentorError}
          </div>
        ) : null}
      </div>
    );
  }

  // ========================================
  // Load lesson via Orchestrator (/lesson/…)
  // include attempt
  // ========================================
  useEffect(() => {
    let cancelled = false;

    async function loadContent() {
      try {
        setLessonLoadError("");
        setLessonLoadDone(false);
        const lessonFetchUrl = `${API}/api/lesson/${weekNumber}/${lessonNumber}?student_id=${encodeURIComponent(
          studentId || "SM01K4R9"
        )}&lesson_id=${encodeURIComponent(lessonId)}&attempt=${encodeURIComponent(String(attempt))}${
          devJump ? "&dev_jump=1" : ""
        }`;
        console.log("LESSON_ROUTE_DEBUG", {
          routeWeek: weekNumber,
          routeLesson: lessonNumber,
          resolvedLessonId: lessonId,
          apiUrl: lessonFetchUrl,
        });
        const res = await fetch(lessonFetchUrl);
        const data = await res.json().catch(() => null);
        if (!res.ok || data?.error) {
          const detail =
            data?.detail ||
            data?.error ||
            `Lesson fetch failed (${res.status})`;
          throw new Error(detail);
        }

        if (cancelled) return;
        if (data?.blocked_by_weekly_reflection) {
          const pendingWeek = Number(data?.pending_reflection_week);
          const targetWeek = Number.isNaN(pendingWeek) ? weekNumber : pendingWeek;
          navigate(`${basePrefix}/week-reflection/${targetWeek}`, { replace: true });
          return;
        }

        if (data?.week_intro_required) {
          setWeekIntroRequired(true);
          setWeekIntroData(data?.week_intro || null);
          setStartWeekError("");
          setLessonContent(null);
          setCoachTips([]);
          setExpandedPerformanceDimension(null);
          setTooltipPerformanceDimension(null);
          return;
        }

        setWeekIntroRequired(false);
        setWeekIntroData(null);

        setLessonContent(data?.lesson_data || null);
        setCoachTips(Array.isArray(data?.coach_tips) ? data.coach_tips : []);
        setExpandedPerformanceDimension(null);
        setTooltipPerformanceDimension(null);
      } catch (err) {
        console.error("Failed loading lesson:", err);
        if (!cancelled) {
          setLessonContent(null);
          setLessonLoadError("Could not load lesson content.");
        }
      } finally {
        if (!cancelled) setLessonLoadDone(true);
      }
    }

    loadContent();
    return () => {
      cancelled = true;
    };
  }, [
    weekNumber,
    lessonNumber,
    lessonId,
    studentId,
    attempt,
    basePrefix,
    navigate,
    location.search,
    reloadTick,
    lessonResetSignal,
    devJump,
  ]);

  // ========================================
  // Load dashboard metadata
  // ========================================
  const refreshCanonicalState = useCallback(async () => {
    if (!studentId || canonicalRefreshInFlightRef.current) return;
    const now = Date.now();
    if (now - canonicalRefreshAtRef.current < 750) return;
    canonicalRefreshInFlightRef.current = true;
    canonicalRefreshAtRef.current = now;
    try {
      const [dashboardRes, lessonStateRes] = await Promise.all([
        fetch(`${API}/api/dashboard?student_id=${encodeURIComponent(studentId)}`, {
          cache: "no-store",
        }),
        fetch(`${API}/api/lesson-state/${encodeURIComponent(studentId)}`, {
          cache: "no-store",
        }),
      ]);
      const [data, lessonState] = await Promise.all([
        dashboardRes.json().catch(() => null),
        lessonStateRes.json().catch(() => null),
      ]);
      if (data) {
        setDashboardData(data);
        setRequiresWeekReflection(Boolean(data.pending_weekly_reflection));
        const pendingWeek = Number(data.pending_reflection_week);
        setPendingReflectionWeek(Number.isNaN(pendingWeek) ? null : pendingWeek);
      }
      const lessons = Array.isArray(lessonState?.lessons) ? lessonState.lessons : [];
      const hit = lessons.find(
        (row) => Number(row?.week) === Number(weekNumber) && Number(row?.lesson) === Number(lessonNumber)
      );
      if (process.env.NODE_ENV !== "production") {
        console.debug("FEEDBACK_COMPLETED_STATE_REFRESHED", {
          lessonId: lessonContentId,
          completed: Boolean(hit?.closed),
        });
      }
      setLessonClosed(Boolean(hit?.closed));
    } finally {
      canonicalRefreshInFlightRef.current = false;
    }
  }, [lessonContentId, studentId, weekNumber, lessonNumber]);

  useEffect(() => {
    refreshCanonicalState().catch((err) => {
      console.error("Canonical progress load error:", err);
      setDashboardData(null);
      setRequiresWeekReflection(false);
      setPendingReflectionWeek(null);
    });
  }, [refreshCanonicalState, location.search, reloadTick, lessonResetSignal]);

  useEffect(() => {
    const refetchWhenVisible = () => {
      if (document.visibilityState === "visible") {
        refreshCanonicalState().catch(() => {});
      }
    };
    window.addEventListener("focus", refetchWhenVisible);
    document.addEventListener("visibilitychange", refetchWhenVisible);
    return () => {
      window.removeEventListener("focus", refetchWhenVisible);
      document.removeEventListener("visibilitychange", refetchWhenVisible);
    };
  }, [refreshCanonicalState]);

  // Guard against opening a locked lesson URL directly.
  // The DB cursor from /api/dashboard is authoritative.
  useEffect(() => {
    if (!dashboardData || devJump || adminLessonTestModeEnabled === null || isAdminLessonTestMode) return;

    // A completed submission may advance the backend cursor immediately. Keep
    // the student on this lesson while its feedback is being processed or read.
    if (feedbackPhase !== "idle" || feedback) return;

    const currentWeek = parseInt(String(dashboardData?.week ?? "1"), 10) || 1;
    const currentLesson = parseInt(String(dashboardData?.lesson ?? "1"), 10) || 1;

    const isAheadOfCurrent =
      weekNumber > currentWeek ||
      (weekNumber === currentWeek && lessonNumber > currentLesson);

    if (!isAheadOfCurrent) return;

    navigate(`${basePrefix}/week/${currentWeek}/lesson/${currentLesson}`, {
      replace: true,
    });
  }, [
    dashboardData,
    weekNumber,
    lessonNumber,
    basePrefix,
    navigate,
    devJump,
    adminLessonTestModeEnabled,
    isAdminLessonTestMode,
    feedbackPhase,
    feedback,
  ]);

  // ========================================
  // Reset when lesson changes OR query changes
  // ========================================
  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    submitInFlightRef.current = false;
    setDashboardData(null);
    setWeekIntroRequired(false);
    setWeekIntroData(null);
    setStartingWeek(false);
    setStartWeekError("");
    setReloadTick(0);
    setLessonLoadError("");
    setLessonLoadDone(false);
    setText("");
    retryEditingRef.current = false;
    setRetryEditing(false);
    attemptOneDraftRef.current = { text: "", structuredSubmission: {} };
    setFeedback(null);
    setEmotionCurveView("before");
    setEmotionCurveAnimationKey(0);
    let chartIntroSeen = weekNumber === 4 && lessonNumber === 4;
    try {
      chartIntroSeen =
        chartIntroSeen && localStorage.getItem(W4L4_CHART_INTRO_SEEN_KEY) === "1";
    } catch (error) {
      chartIntroSeen = false;
    }
    setW4l4ChartIntroPhase(chartIntroSeen ? "complete" : "idle_waiting_for_view");
    setW4l4VisiblePointCount(chartIntroSeen ? W4L4_CHART_POINT_COUNT : 0);
    setStoryReadToEnd(false);
    setStoryNextPressed(false);
    setGroundedFeedback(null);
    setFeedbackMeta({
      score: null,
      passScore: null,
      status: null,
      attempt: null,
      maxAttempts: null,
      rubric: null,
      evidence: null,
    });
    setCoach(null);
    setMicroLesson(null);
    setSubmitGuardMessage("");
    setShowBelowMinConfirm(false);
    setNextLesson(null);
    setAllowOptionalImprovementAttempt(false);
    setImprovementAttemptLabel("Improve Draft");

    setAnnotations([]);
    setWritingSignalAnnotationSets({ basic: [], enriched: [] });
    setWritingSignalMode(WRITING_SIGNAL_MODES.OFF);

    setMentorTip(null);
    setMentorPlan(null);
    setMentorError(null);

    setLoadingFeedback(false);
    setFeedbackPhase("idle");
    setRubricScore(null);
    setRubricDimensionScores(null);
    setExpandedPerformanceDimension(null);
    setTooltipPerformanceDimension(null);
    setAttemptsAllowed(null);
    setActiveSubmissionId(null);
    setProcessingFailed(false);

    setStatus("idle");
    setDirty(false);
    didHydrateRef.current = false;
    w4l4StartWritingPreloadedRef.current = false;

    setPassed(false);

    setCoachTips([]);
    setExpandedPerformanceDimension(null);
    setTooltipPerformanceDimension(null);

    setGatesEnforced(true);
    setAllowRepeat(false);
    setLearningState(null);
    setCourseCompleted(false);
    setCourseCompleteView(false);
    setShowGraduation(false);
    setRequiresWeekReflection(false);
    setPendingReflectionWeek(null);

    setLessonClosed(false);
    setVideoWatched(false);
    mentorSignalsAutoTriggeredRef.current = false;

    const freshQuery = new URLSearchParams(location.search);
    const a = parseInt(freshQuery.get("attempt") || "1", 10);
    setAttempt(Number.isNaN(a) ? 1 : a);

    if (inPortal) {
      const portalMain = document.querySelector(".sp-main");
      if (portalMain && typeof portalMain.scrollTo === "function") {
        portalMain.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [weekNumber, lessonNumber, location.search, inPortal]);

  useEffect(
    () => () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!showBelowMinConfirm) return undefined;

    function onKeyDown(e) {
      if (e.key === "Escape") setShowBelowMinConfirm(false);
    }

    document.addEventListener("keydown", onKeyDown);
    window.requestAnimationFrame(() => {
      if (belowMinPrimaryRef.current) belowMinPrimaryRef.current.focus();
    });

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showBelowMinConfirm]);

  useEffect(() => {
    let cancelled = false;
    async function loadUiConfig() {
      try {
        const res = await fetch(`${API}/api/config`, {
          cache: "no-store",
          headers: {
            "X-User": getAuthUserHeader(),
          },
        });
        const json = await res.json().catch(() => null);
        if (!cancelled) {
          setVideoGatesEnforced(Boolean(json?.enforce_video_gates ?? true));
          setAdminLessonTestModeEnabled(Boolean(json?.ENABLE_ADMIN_LESSON_TEST_MODE === true));
          const nerHighlightsFlag =
            json?.display_flags?.ner_highlights ?? json?.ner_highlights;
          setNerHighlightsEnabled(nerHighlightsFlag === true);
        }
      } catch {
        if (!cancelled) {
          setVideoGatesEnforced(true);
          setAdminLessonTestModeEnabled(false);
          setNerHighlightsEnabled(false);
        }
      }
    }
    loadUiConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      setFocusMode(sessionStorage.getItem(focusModeSessionKey) === "1");
    } catch {
      setFocusMode(false);
    }
  }, [focusModeSessionKey]);

  useEffect(() => {
    try {
      if (focusMode) sessionStorage.setItem(focusModeSessionKey, "1");
      else sessionStorage.removeItem(focusModeSessionKey);
    } catch {
      // non-blocking if sessionStorage is unavailable
    }
  }, [focusMode, focusModeSessionKey]);

  useEffect(() => {
    if (!setFocusModeActive) return;
    setFocusModeActive(focusMode);
  }, [focusMode, setFocusModeActive]);

  useEffect(
    () => () => {
      if (setFocusModeActive) setFocusModeActive(false);
    },
    [setFocusModeActive]
  );

  useEffect(() => {
    if (!focusMode) return undefined;
    function onKeyDown(e) {
      if (e.key === "Escape" && !showBelowMinConfirm) setFocusMode(false);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [focusMode, showBelowMinConfirm]);

  // If student advanced past lesson, block feedback submission in UI.
  useEffect(() => {
    let cancelled = false;

    async function loadLessonState() {
      try {
        const res = await fetch(
          `${API}/api/lesson-state/${encodeURIComponent(studentId)}`
        );
        const json = await res.json().catch(() => null);
        if (cancelled) return;

        const lessons = Array.isArray(json?.lessons) ? json.lessons : [];
        const hit = lessons.find(
          (r) =>
            Number(r?.week) === Number(weekNumber) &&
            Number(r?.lesson) === Number(lessonNumber)
        );
        setLessonClosed(Boolean(hit?.closed));
      } catch {
        if (!cancelled) setLessonClosed(false);
      }
    }

    if (studentId) loadLessonState();
    return () => {
      cancelled = true;
    };
  }, [studentId, weekNumber, lessonNumber, lessonResetSignal]);

  // Resolve video gate from server status when lesson content is known
  useEffect(() => {
    let cancelled = false;
    async function resolveGate() {
      if (!lessonContent) {
        return;
      }

      const hasVideoNow = !!lessonContent?.introVideoUrl;
      if (!hasVideoNow || isAdminLessonTestMode) {
        setVideoWatched(true);
        return;
      }
      try {
        const res = await fetch(
          `${API}/api/video_watched?student_id=${encodeURIComponent(studentId)}&week=${encodeURIComponent(
            weekNumber
          )}&lesson=${encodeURIComponent(lessonNumber)}`
        );
        const json = await res.json().catch(() => null);
        if (!cancelled) {
          setVideoWatched(Boolean(json?.watched));
        }
      } catch {
        if (!cancelled) {
          setVideoWatched(false);
        }
      }
    }
    resolveGate();
    return () => {
      cancelled = true;
    };
  }, [
    lessonContent,
    lessonResetSignal,
    studentId,
    weekNumber,
    lessonNumber,
    videoGatesEnforced,
    isAdminLessonTestMode,
  ]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    function handleLessonReset(event) {
      const resetDetail = event?.detail || {};
      w1l1StaleRefreshRef.current = 0;
      setFeedbackSourceEndpoint("");
      clearLessonCacheArtifacts({
        studentId: resetDetail.studentId,
        week: resetDetail.week,
        lesson: resetDetail.lesson,
      });
      setText("");
      setStructuredSubmission({});
      setStatus("idle");
      setDirty(false);
      setDashboardData(null);
      setLessonClosed(false);
      setLessonLoadDone(false);
      setFeedback(null);
      setGroundedFeedback(null);
      setFeedbackMeta({
        score: null,
        passScore: null,
        status: null,
        attempt: null,
        maxAttempts: null,
        rubric: null,
        evidence: null,
      });
      setCoach(null);
      setMicroLesson(null);
      setAnnotations([]);
      setWritingSignalAnnotationSets({ basic: [], enriched: [] });
      setMentorTip(null);
      setMentorPlan(null);
      setMentorError(null);
      setProcessingFailed(false);
      setActiveSubmissionId(null);
      setLoadingFeedback(false);
      setFeedbackPhase("idle");
      setSubmitGuardMessage("");
      setLessonResetSignal((n) => n + 1);
      setVideoWatched(false);
    }

    window.addEventListener("writeflow:lesson-reset", handleLessonReset);
    return () => {
      window.removeEventListener("writeflow:lesson-reset", handleLessonReset);
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!lessonLoadDone || lessonResetSignal <= 0) return;

    let cancelled = false;
    async function refreshAfterReset() {
      stopSubmissionPolling();
      clearRenderableFeedbackState();
      setActiveSubmissionId(null);
      setProcessingFailed(false);
      setLoadingFeedback(false);
      setFeedbackPhase("idle");
      setSubmitGuardMessage("");
      clearSubmissionRecoveryKey();
      if (!cancelled) {
        await recoverLatestSubmission({ allowCompleted: true, surfaceFailed: false });
      }
    }

    refreshAfterReset();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonResetSignal, lessonLoadDone]);

  // ========================================
  // Load saved draft from localStorage (per lesson)
  // ========================================
  useEffect(() => {
    try {
      const saved = localStorage.getItem(draftKey);
      const savedStructured = localStorage.getItem(structuredDraftKey);
      let parsedStructured = {};
      if (savedStructured) {
        try {
          const parsed = JSON.parse(savedStructured);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            parsedStructured = parsed;
          }
        } catch {
            parsedStructured = {};
        }
      }
      if (
        hasStructuredSubmission &&
        Object.keys(parsedStructured).length === 0 &&
        saved
      ) {
        parsedStructured = parseStructuredSubmissionText(submissionSections, saved);
      }
      setStructuredSubmission(
        hasStructuredSubmission ? stripLeakedImplementationText(parsedStructured, lessonContent) : {}
      );
      const savedText = String(saved || "");
      const ignoreSavedNarrativeDraft =
        isNarrativeCurveRewrite &&
        /\bavoid:\s*failed\b/i.test(savedText);
      const ignoreLeakedDraft =
        lessonContentId === "W4L4" && hasLeakedImplementationText(savedText);
      if (savedText.trim().length > 0 && !ignoreSavedNarrativeDraft && !ignoreLeakedDraft) {
        setText(saved);
        setStatus("saved_local");
      } else {
        if (ignoreSavedNarrativeDraft || ignoreLeakedDraft) {
          localStorage.removeItem(draftKey);
          localStorage.removeItem(structuredDraftKey);
        }
        setText("");
        setStatus("idle");
      }
    } catch (e) {
      console.warn("localStorage read failed:", e);
      setText("");
      setStructuredSubmission({});
      setStatus("idle");
    } finally {
      didHydrateRef.current = true;
      setDirty(false);
    }
  }, [
    draftKey,
    structuredDraftKey,
    hasStructuredSubmission,
    submissionSections,
    lessonContent,
    lessonContentId,
    isNarrativeCurveRewrite,
    originalRewritePassage,
  ]);

  // ========================================
  // Load saved draft from backend (if available)
  // ========================================
  useEffect(() => {
    if (!didHydrateRef.current) return;
    if (dirty) return;

    let cancelled = false;

    async function loadOnlineDraft() {
      try {
        const res = await fetch(
          `${API}/api/load_draft/${encodeURIComponent(studentId)}/${weekNumber}/${lessonNumber}`
        );
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        if (cancelled || !data) return;

        const draftText = String(data.draft_text ?? data.draft ?? "");
        if (!draftText.trim()) return;
        if (isNarrativeCurveRewrite && /\bavoid:\s*failed\b/i.test(draftText)) {
          localStorage.removeItem(draftKey);
          localStorage.removeItem(structuredDraftKey);
          return;
        }
        if (lessonContentId === "W4L4" && hasLeakedImplementationText(draftText)) {
          localStorage.removeItem(draftKey);
          localStorage.removeItem(structuredDraftKey);
          return;
        }

        let nextStructured = {};
        if (hasStructuredSubmission) {
          nextStructured = normalizeStructuredSubmission(data.structured_submission);
          if (Object.keys(nextStructured).length === 0) {
            nextStructured = parseStructuredSubmissionText(submissionSections, draftText);
          }
        }

        setText(draftText);
        setStructuredSubmission(stripLeakedImplementationText(nextStructured, lessonContent));
        if (hasStructuredSubmission) {
          localStorage.setItem(
            structuredDraftKey,
            JSON.stringify(stripLeakedImplementationText(nextStructured, lessonContent))
          );
        } else {
          localStorage.removeItem(structuredDraftKey);
        }
        localStorage.setItem(draftKey, draftText);
        setStatus("saved_online");
        setDirty(false);
      } catch (e) {
        console.warn("online draft load failed:", e);
      }
    }

    loadOnlineDraft();
    return () => {
      cancelled = true;
    };
  }, [
    studentId,
    weekNumber,
    lessonNumber,
    draftKey,
    structuredDraftKey,
    dirty,
    hasStructuredSubmission,
    submissionSections,
    lessonContent,
    lessonContentId,
    isNarrativeCurveRewrite,
    readOnly,
  ]);

  // ========================================
  // Text typing
  // ========================================
  function applyTextUpdate(nextText, source = "typing") {
    if (lessonLockedByVideo) return;
    const normalized = String(nextText || "");
    const clipped = normalized.slice(0, MAX_RESPONSE_CHARS);
    if (source === "paste") {
      setPasteNotice(
        clipped.length < normalized.length
          ? `Pasted text was trimmed to ${MAX_RESPONSE_CHARS} characters to keep the page responsive.`
          : ""
      );
    } else if (pasteNotice) {
      setPasteNotice("");
    }
    setText(clipped);
    setDirty(true);
    setStatus(clipped.trim().length === 0 ? "idle" : "typing");
    if (submitGuardMessage) setSubmitGuardMessage("");
  }

  function handleTextChange(e) {
    if (lessonLockedByVideo) return;
    if (hasStructuredSubmission) {
      return;
    }
    applyTextUpdate(e.target.value, "typing");
  }

  function handleStructuredSectionChange(sectionKey, nextValue) {
    if (lessonLockedByVideo) return;
    const nextStructured = {
      ...structuredSubmission,
      [sectionKey]: String(nextValue || "").slice(0, MAX_RESPONSE_CHARS),
    };
    setStructuredSubmission(nextStructured);
    applyTextUpdate(
      composeStructuredSubmissionText(submissionSections, nextStructured),
      "typing"
    );
  }

  function handleTextPaste(e) {
    if (lessonLockedByVideo) {
      e.preventDefault();
      return;
    }
    const pasted = e?.clipboardData?.getData("text");
    if (typeof pasted !== "string") return;

    e.preventDefault();

    const target = e.target;
    const start = Number.isInteger(target.selectionStart)
      ? target.selectionStart
      : text.length;
    const end = Number.isInteger(target.selectionEnd)
      ? target.selectionEnd
      : text.length;

    const selectedLength = Math.max(0, end - start);
    const availableSpace = Math.max(
      0,
      MAX_RESPONSE_CHARS - (text.length - selectedLength)
    );
    const clippedPaste = pasted.slice(0, availableSpace);
    const nextText = text.slice(0, start) + clippedPaste + text.slice(end);
    applyTextUpdate(nextText, "paste");

    const nextCursor = start + clippedPaste.length;
    window.requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.selectionStart = nextCursor;
        textareaRef.current.selectionEnd = nextCursor;
      }
    });
  }

  // ========================================
  // Auto-save local (debounced)
  // ========================================
  useEffect(() => {
    if (readOnly || !didHydrateRef.current) return;
    if (!dirty) return;

    const t = setTimeout(() => {
      try {
        if (effectiveDraftText.trim().length === 0) {
          localStorage.removeItem(draftKey);
          localStorage.removeItem(structuredDraftKey);
          setStatus("idle");
        } else {
          setStatus("saving_local");
          localStorage.setItem(draftKey, effectiveDraftText);
          if (hasStructuredSubmission) {
            localStorage.setItem(structuredDraftKey, JSON.stringify(structuredSubmission));
          } else {
            localStorage.removeItem(structuredDraftKey);
          }
          setStatus("saved_local");
        }
      } catch (e) {
        console.warn("localStorage write failed:", e);
        setStatus("error");
      }
    }, 250);

    return () => clearTimeout(t);
  }, [
    effectiveDraftText,
    draftKey,
    structuredDraftKey,
    dirty,
    hasStructuredSubmission,
    structuredSubmission,
    isNarrativeCurveRewrite,
    readOnly,
  ]);

  // ========================================
  // Auto-save online (debounced)
  // ========================================
  useEffect(() => {
    if (readOnly || !didHydrateRef.current) return;
    if (!dirty) return;

    const t = setTimeout(async () => {
      if (effectiveDraftText.trim().length === 0) return;

      try {
        setStatus("saving_online");

        const res = await fetch(DRAFT_SAVE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            student_id: studentId,
            week: weekNumber,
            lesson: lessonNumber,
            draft_text: effectiveDraftText,
            text: effectiveDraftText,
            structured_submission: hasStructuredSubmission
              ? structuredSubmission
              : null,
          }),
        });

        if (!res.ok) {
          setStatus("offline");
          return;
        }

        setDirty(false);
        setStatus("saved_online");
      } catch (e) {
        console.warn("online save failed:", e);
        setStatus("offline");
      }
    }, 1200);

    return () => clearTimeout(t);
  }, [
    effectiveDraftText,
    dirty,
    studentId,
    weekNumber,
    lessonNumber,
    DRAFT_SAVE_URL,
    hasStructuredSubmission,
    structuredSubmission,
    isNarrativeCurveRewrite,
    readOnly,
  ]);

  // ========================================
  // Normalize "next lesson" from backend
  // ========================================
  function parseNextLessonFromResponse(data) {
    const nl =
      data?.next_lesson ??
      data?.nextLesson ??
      (data?.next?.type === "lesson" ? data.next : null);

    if (nl && typeof nl === "object" && nl.week != null && nl.lesson != null) {
      return { week: Number(nl.week), lesson: Number(nl.lesson) };
    }
    if (typeof nl === "number") {
      return { week: weekNumber, lesson: nl };
    }
    if (typeof nl === "string" && nl.trim()) {
      const n = parseInt(nl, 10);
      if (!Number.isNaN(n)) return { week: weekNumber, lesson: n };
    }
    return null;
  }

  function buildLessonPath(target) {
    return `${basePrefix}/week/${target.week}/lesson/${target.lesson}`;
  }

  function getCurrentCursorTarget() {
    const currentWeek = parseInt(String(dashboardData?.week ?? weekNumber), 10) || weekNumber;
    const currentLesson = parseInt(String(dashboardData?.lesson ?? lessonNumber), 10) || lessonNumber;
    return { week: currentWeek, lesson: currentLesson };
  }

  async function openPendingReflection(pendingReflectionTargetWeek = null) {
    const targetWeek =
      Number(pendingReflectionTargetWeek) ||
      Number(pendingReflectionWeek) ||
      Number(dashboardData?.pending_reflection_week) ||
      weekNumber;
    const marked = await markCurrentLessonCompleteBeforeReflection({
      studentId,
      week: weekNumber,
      lesson: lessonNumber,
    });
    if (!marked) {
      setStatus("error");
      setSubmitGuardMessage("Feedback must be stored before weekly reflection can open.");
      return false;
    }
    navigate(`${basePrefix}/week-reflection/${targetWeek}`);
    return true;
  }

  async function fetchCurrentCursorTarget() {
    try {
      const res = await fetch(`${API}/api/dashboard?student_id=${encodeURIComponent(studentId)}`);
      const data = await res.json().catch(() => null);
      const currentWeek = parseInt(String(data?.week ?? weekNumber), 10) || weekNumber;
      const currentLesson = parseInt(String(data?.lesson ?? lessonNumber), 10) || lessonNumber;
      return { week: currentWeek, lesson: currentLesson };
    } catch {
      return getCurrentCursorTarget();
    }
  }

  function isViewingPastLesson() {
    const current = getCurrentCursorTarget();
    return (
      current.week > weekNumber ||
      (current.week === weekNumber && current.lesson > lessonNumber)
    );
  }

  function clearRenderableFeedbackState() {
    stopFeedbackProgressStream();
    resetProgressPanelState();
    setFeedback(null);
    setGroundedFeedback(null);
    setFeedbackMeta({
      score: null,
      passScore: null,
      status: null,
      attempt: null,
      maxAttempts: null,
      rubric: null,
      evidence: null,
    });
    setCoach(null);
    setMicroLesson(null);
    setNextLesson(null);
    setAllowOptionalImprovementAttempt(false);
    setImprovementAttemptLabel("Improve Draft");
    setAnnotations([]);
    setWritingSignalAnnotationSets({ basic: [], enriched: [] });
    setWritingSignalMode(WRITING_SIGNAL_MODES.OFF);
    setMentorTip(null);
    setMentorPlan(null);
    setMentorError(null);
    setPassed(false);
    setAllowRepeat(false);
    setLearningState(null);
    setCourseCompleted(false);
    setShowGraduation(false);
    setRequiresWeekReflection(false);
    setPendingReflectionWeek(null);
    setRubricScore(null);
    setRubricDimensionScores(null);
    setExpandedPerformanceDimension(null);
    setTooltipPerformanceDimension(null);
    setAttemptsAllowed(null);
  }

  // ========================================
  // Retry Attempt 2 (no reflection page)
  // ========================================
  function goToRetryAttempt2() {
    const savedDraft = {
      text: String(text || ""),
      structuredSubmission: { ...(structuredSubmission || {}) },
    };
    if (!savedDraft.text.trim()) {
      try {
        savedDraft.text = String(localStorage.getItem(draftKey) || "");
        const savedStructured = localStorage.getItem(structuredDraftKey);
        if (savedStructured) {
          const parsed = JSON.parse(savedStructured);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            savedDraft.structuredSubmission = parsed;
          }
        }
      } catch {
        // The in-memory draft remains the fallback.
      }
    }
    attemptOneDraftRef.current = savedDraft;
    retryEditingRef.current = true;
    setRetryEditing(true);
    setShowImproveDraftConfirm(false);
    stopFeedbackProgressStream();
    setAttempt(2);

    // Clear feedback UI so student can submit again
    setFeedback(null);
    setEmotionCurveView("before");
    setEmotionCurveAnimationKey(0);
    setW4l4ChartIntroPhase(w4l4ChartIntroSeen ? "complete" : "idle_waiting_for_view");
    setW4l4VisiblePointCount(w4l4ChartIntroSeen ? W4L4_CHART_POINT_COUNT : 0);
    setStoryReadToEnd(false);
    setStoryNextPressed(false);
    setCoach(null);
    setMicroLesson(null);
    setNextLesson(null);
    setAllowOptionalImprovementAttempt(false);
    setImprovementAttemptLabel("Improve Draft");
    setAnnotations([]);
    setWritingSignalAnnotationSets({ basic: [], enriched: [] });
    setMentorTip(null);
    setMentorPlan(null);
    setMentorError(null);
    setCourseCompleted(false);
    setShowGraduation(false);
    setFeedbackPhase("idle");
    setRubricScore(null);
    setAttemptsAllowed(null);
    setProcessingFailed(false);
    setActiveSubmissionId(null);
    clearSubmissionRecoveryKey();

    // Restore the submitted Attempt 1 draft after the feedback UI is hidden.
    // This state is intentionally independent of Mentor output or feedback stages.
    setText(savedDraft.text);
    setStructuredSubmission(savedDraft.structuredSubmission);
    setDirty(false);

    setStatus(savedDraft.text && savedDraft.text.trim().length > 0 ? "typing" : "idle");

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleRetryOrImproveClick() {
    if (passed && canOptionalImproveAttempt2) {
      setShowImproveDraftConfirm(true);
      return;
    }
    goToRetryAttempt2();
  }

  // ========================================
  // Submit for feedback
  // ========================================
  function saveSubmissionRecoveryKey(submissionId) {
    try {
      localStorage.removeItem(legacySubmissionRecoveryKey);
      localStorage.setItem(submissionRecoveryKey, String(submissionId));
      localStorage.setItem(submissionRecoveryVersionKey, CURRENT_FEEDBACK_UI_VERSION);
    } catch {
      // non-blocking
    }
  }

  function stopSubmissionPolling() {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  function beginFeedbackRequest() {
    feedbackRequestRef.current += 1;
    return feedbackRequestRef.current;
  }

  function hasRenderableEvaluationPayload(data) {
    if (!data || typeof data !== "object") return false;
    if (data.error) return false;
    if (data.ok === false) return false;

    const resolvedScore =
      data?.overall_score ?? data?.score ?? data?.rubric_score ?? null;
    const hasScore = Number.isFinite(Number(resolvedScore));

    const hasDimensionScores =
      data?.dimension_scores &&
      typeof data.dimension_scores === "object" &&
      !Array.isArray(data.dimension_scores);

    const hasLegacyFeedback =
      data?.feedback &&
      typeof data.feedback === "object" &&
      !Array.isArray(data.feedback);

    return Boolean(hasScore && (hasDimensionScores || hasLegacyFeedback));
  }

  function applyCompletedFeedback(data, sourceEndpoint = "") {
    if (retryEditingRef.current) return;
    if (!hasRenderableEvaluationPayload(data)) return;
    if (process.env.NODE_ENV !== "production") {
      console.debug("FEEDBACK_RESULT_RECEIVED", {
        lessonId: lessonContentId,
        sourceEndpoint,
        score: data?.score ?? data?.overall_score ?? data?.rubric_score ?? null,
      });
      console.debug("ADMIN_DETAILS_RENDER_INPUT", {
        sourceEndpoint,
        canonicalRole: String(canonicalAuthUser?.role || "").trim().toLowerCase(),
        hasAdminDetails: Boolean(data?.admin_details),
        nestedAdminDetails: Boolean(data?.result_json?.admin_details),
      });
    }
    stopFeedbackProgressStream();
    if (isKnownStaleW1L1FeedbackPayload(lessonContentId, data)) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("W1L1 stale feedback blocked before render.", {
          lessonId: lessonContentId,
          sourceEndpoint,
          score: Number(data?.score ?? data?.overall_score ?? data?.rubric_score ?? null),
        });
      }
      clearRenderableFeedbackState();
      if (w1l1StaleRefreshRef.current < 1) {
        w1l1StaleRefreshRef.current += 1;
        void recoverLatestSubmission({ allowCompleted: true, surfaceFailed: false });
      }
      stopSubmissionPolling();
      setProcessingFailed(true);
      setLoadingFeedback(false);
      setFeedbackPhase("idle");
      submitInFlightRef.current = false;
      setStatus("error");
      setSubmitGuardMessage("Processing failed. Retry processing / reload result safely.");
      return;
    }
    // Invalidate older async handlers (polling / submit responses).
    feedbackRequestRef.current += 1;
    w1l1StaleRefreshRef.current = 0;

    clearSubmissionRecoveryKey();
    stopSubmissionPolling();
    setProcessingFailed(false);
    setSubmitGuardMessage("");
    setLoadingFeedback(false);
    submitInFlightRef.current = false;
    setFeedbackSourceEndpoint(String(sourceEndpoint || ""));
    try {
      localStorage.setItem(submissionRecoveryVersionKey, CURRENT_FEEDBACK_UI_VERSION);
    } catch {
      // non-blocking
    }
    setActiveSubmissionId(
      Number.isFinite(Number(data?.submission_id))
        ? Number(data.submission_id)
        : null
    );

    if (typeof data.rubric_score === "number") setRubricScore(data.rubric_score);
    setRubricDimensionScores(
      data?.dimension_scores &&
        typeof data.dimension_scores === "object" &&
        !Array.isArray(data.dimension_scores)
        ? { ...data.dimension_scores }
        : null
    );
    setExpandedPerformanceDimension(null);
    setTooltipPerformanceDimension(null);
    if (typeof data.attempts_allowed === "number")
      setAttemptsAllowed(data.attempts_allowed);
    if (typeof data.gates_enforced === "boolean")
      setGatesEnforced(data.gates_enforced);

    setPassed(!!data.passed);
    setAllowRepeat(!!data.allow_repeat);
    setAllowOptionalImprovementAttempt(!!data.allow_optional_improvement_attempt);
    setImprovementAttemptLabel(
      String(data.improvement_attempt_label || "").trim() || "Improve Draft"
    );
    setLearningState(data.learning_state || null);
    setRequiresWeekReflection(Boolean(data?.requires_week_reflection));
    const pendingWeek = Number(data?.pending_reflection_week);
    setPendingReflectionWeek(Number.isNaN(pendingWeek) ? null : pendingWeek);
    const completed = Boolean(data?.course_completed);
    const graduation = completed || Boolean(data?.show_graduation);
    setCourseCompleted(completed);
    setShowGraduation(graduation);

    if (typeof data.attempt === "number") setAttempt(data.attempt);

    const nextCoachFeedback = normalizeCoachFeedbackContract(data?.student_feedback);
    if (process.env.NODE_ENV !== "production") {
      const missingCoachFields = ["what_worked", "what_holding_back", "next_step_message"].filter(
        (key) => !String(nextCoachFeedback?.[key] || "").trim()
      );
      if (!Object.prototype.hasOwnProperty.call(data?.student_feedback || {}, "coach_available") && !nextCoachFeedback?.coach_feedback_paragraph && missingCoachFields.length) {
        console.warn("COACH_RENDER_WARN: missing coach fields", missingCoachFields);
      }
      const leakedKeys = ["evidence_lines", "evidence_anchors", "draft_evidence", "signals_used", "internal_notes"].filter(
        (key) => Object.prototype.hasOwnProperty.call(data?.student_feedback || {}, key)
      );
      if (leakedKeys.length) {
        console.warn("COACH_RENDER_WARN: internal coach fields leaked", leakedKeys);
      }
    }

    const nextFeedback = {
      ...(data.feedback || {
        creativity: "No feedback returned from server.",
        clarity: "",
        task_completion: "",
      }),
      student_feedback: nextCoachFeedback,
      status: String(data?.status || "").toLowerCase() || null,
      display_frame:
        data?.display_frame && typeof data.display_frame === "object"
          ? data.display_frame
          : null,
      score: Number.isFinite(Number(data?.score)) ? Number(data.score) : null,
      pass_score: Number.isFinite(Number(data?.pass_score))
        ? Number(data.pass_score)
        : null,
      story_curve_update:
        data?.story_curve_update && typeof data.story_curve_update === "object"
          ? data.story_curve_update
          : null,
      admin_details:
        data?.admin_details && typeof data.admin_details === "object"
          ? data.admin_details
          : null,
    };
    const nextGrounded = nextCoachFeedback || null;
    const nextMeta = {
      score: Number.isFinite(Number(data?.score)) ? Number(data.score) : null,
      passScore: Number.isFinite(Number(data?.pass_score))
        ? Number(data.pass_score)
        : null,
      status: String(data?.status || "").toLowerCase() || null,
      attempt: Number.isFinite(Number(data?.attempt)) ? Number(data.attempt) : null,
      maxAttempts: Number.isFinite(Number(data?.max_attempts))
        ? Number(data.max_attempts)
        : null,
      rubric: data?.rubric && typeof data.rubric === "object" ? data.rubric : null,
      evidence:
        data?.evidence && typeof data.evidence === "object" ? data.evidence : null,
      displayFrame:
        data?.display_frame && typeof data.display_frame === "object"
          ? data.display_frame
          : null,
    };

    const annotationSets = normalizeWritingSignalAnnotationSets(data);
    const signalDisplayConfig = normalizeWritingSignalDisplayConfig(data);
    const nextAnnotations = dedupeWritingSignalAnnotations([
      ...annotationSets.basic,
      ...annotationSets.enriched,
    ]);
    if (process.env.NODE_ENV !== "production") {
      const signalSource = String(data?.writing_signals?.summary?.source || "unknown");
      console.debug(
        "WRITING_SIGNALS_RENDER:",
        `source=${signalSource}`,
        `basic=${annotationSets.basic.length}`,
        `enriched=${annotationSets.enriched.length}`,
        `annotations=${nextAnnotations.length}`
      );
    }
    setAnnotations(nextAnnotations);
    setWritingSignalAnnotationSets(annotationSets);
    setWritingSignalDisplayConfig(signalDisplayConfig);
    const signalAvailability = getWritingSignalModeAvailability(
      lessonContentId,
      nextAnnotations,
      signalDisplayConfig.lessonSignalDisplay,
      signalDisplayConfig.lessonSignalMap
    );
    setWritingSignalMode(
      signalAvailability.hasBasic
        ? WRITING_SIGNAL_MODES.BASIC
        : signalAvailability.hasEnriched
        ? WRITING_SIGNAL_MODES.ENRICHED
        : WRITING_SIGNAL_MODES.OFF
    );
    setCoach(data.coach || null);

    if (data.next?.type === "micro") setMicroLesson(data.next);
    else setMicroLesson(null);

    const parsedNext = parseNextLessonFromResponse(data);
    setNextLesson(graduation ? null : parsedNext || null);

    setMentorTip(data?.mentor?.mentor_tip || null);
    setMentorPlan(data?.mentor?.mentor_plan || null);
    setMentorError(data?.mentor?.mentor_error || null);
    if (data?.llm_eval_used === false) {
      console.debug("Feedback rendered with deterministic fallback (llm_eval_used=false).");
    }

    setStatus("submitted");
    // Store the result before refreshing canonical completion state. The
    // completed-lesson guard must never outrank feedback from this request.
    if (process.env.NODE_ENV !== "production") {
      console.debug("FEEDBACK_SET_CALLED", { lessonId: lessonContentId });
    }
    setFeedback(nextFeedback);
    setGroundedFeedback(nextGrounded);
    setFeedbackMeta(nextMeta);
    setFeedbackPhase("show");
    window.setTimeout(() => {
      const node = document.getElementById("feedback-card");
      if (node) node.scrollIntoView({ behavior: "smooth" });
    }, 150);
    try {
      window.dispatchEvent(
        new CustomEvent("studentProgressRefetch", {
          detail: {
            studentId,
            currentWeek: weekNumber,
            currentLesson: lessonNumber,
          },
        })
      );
      void refreshCanonicalState();
    } catch {
      // non-blocking
    }
  }

  applyCompletedFeedbackRef.current = applyCompletedFeedback;

  async function fetchSubmissionJson(url, sourceEndpoint) {
    try {
      const res = await fetch(url, {
        cache: "no-store",
        headers: { "X-User": getAuthUserHeader() },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data) return null;

      const resultJson = data?.result_json;
      const shouldRetryFreshness =
        lessonContentId === "W1L1" &&
        data?.status === "completed" &&
        resultJson &&
        typeof resultJson === "object" &&
        shouldRefreshW1L1FeedbackPayload(lessonContentId, resultJson) &&
        w1l1StaleRefreshRef.current < 1;

      if (shouldRetryFreshness) {
        w1l1StaleRefreshRef.current += 1;
        if (process.env.NODE_ENV !== "production") {
          console.debug("W1L1 stale feedback payload detected; refetching.", {
            sourceEndpoint,
            lessonId: lessonContentId,
            scoreRendered: Number(resultJson?.score ?? resultJson?.overall_score ?? resultJson?.rubric_score ?? null),
            feedbackVersion: String(resultJson?.evaluation_version || resultJson?.feedback_ui_version || ""),
          });
        }
        try {
          const retryUrl = `${API}/api/submissions/latest?student_id=${encodeURIComponent(
            studentId
          )}&week=${weekNumber}&lesson=${lessonNumber}`;
          const retryRes = await fetch(retryUrl, {
            cache: "no-store",
            headers: { "X-User": getAuthUserHeader() },
          });
          const retryData = await retryRes.json().catch(() => null);
          if (retryRes.ok && retryData) return retryData;
          if (process.env.NODE_ENV !== "production") {
            console.error("W1L1 stale feedback refresh fell back to cached payload.", {
              sourceEndpoint,
              lessonId: lessonContentId,
            });
          }
        } catch (refreshErr) {
          console.error("W1L1 stale feedback refresh failed.", refreshErr);
        }
      }

      return data;
    } catch (err) {
      console.error("Feedback status fetch failed.", { sourceEndpoint, error: err });
      return null;
    }
  }

  async function fetchSubmissionStatus(submissionId) {
    const url = `${API}/api/submissions/${encodeURIComponent(submissionId)}`;
    return fetchSubmissionJson(url, url);
  }

  async function recoverLatestSubmission({ allowCompleted = true, surfaceFailed = true } = {}) {
    if (retryEditingRef.current) return false;
    const url = `${API}/api/submissions/latest?student_id=${encodeURIComponent(
      studentId
    )}&week=${weekNumber}&lesson=${lessonNumber}`;
    const data = await fetchSubmissionJson(url, url);
    if (!data || data.found === false) return false;

    if (data.status === "processing" && data.submission_id) {
      saveSubmissionRecoveryKey(data.submission_id);
      setActiveSubmissionId(Number(data.submission_id));
      setProcessingFailed(false);
      setSubmitGuardMessage("Processing feedback...");
      setStatus("submitting");
      setLoadingFeedback(true);
      setFeedbackPhase("loading");
      submitInFlightRef.current = true;
      startSubmissionPolling(Number(data.submission_id));
      return true;
    }

    if (
      allowCompleted &&
      data.status === "completed" &&
      data.result_json &&
      typeof data.result_json === "object"
    ) {
      if (hasRenderableEvaluationPayload(data.result_json)) {
        if (shouldRefreshW1L1FeedbackPayload(lessonContentId, data.result_json)) {
          if (process.env.NODE_ENV !== "production") {
            console.debug("W1L1 stale feedback remained stale after retry.", {
              lessonId: lessonContentId,
              sourceEndpoint: url,
            });
          }
        }
        applyCompletedFeedback(data.result_json, url);
        return true;
      }
      if (lessonContentId === "W1L1" && data.result_json) {
        if (process.env.NODE_ENV !== "production") {
          console.error("W1L1 completed payload lacked renderable feedback; using cached result.", {
            lessonId: lessonContentId,
            sourceEndpoint: url,
          });
        }
        applyCompletedFeedback(data.result_json, url);
        return true;
      }
      setActiveSubmissionId(
        Number.isFinite(Number(data?.submission_id))
          ? Number(data.submission_id)
          : null
      );
      clearRenderableFeedbackState();
      setProcessingFailed(true);
      setLoadingFeedback(false);
      setFeedbackPhase("idle");
      submitInFlightRef.current = false;
      setStatus("error");
      setSubmitGuardMessage(
        "Processing failed. Retry processing / reload result safely."
      );
      return true;
    }

    if (data.status === "failed") {
      if (!surfaceFailed) return false;
      setActiveSubmissionId(
        Number.isFinite(Number(data?.submission_id))
          ? Number(data.submission_id)
          : null
      );
      clearRenderableFeedbackState();
      setProcessingFailed(true);
      setLoadingFeedback(false);
      setFeedbackPhase("idle");
      submitInFlightRef.current = false;
      setStatus("error");
      setSubmitGuardMessage(
        "Processing failed. Retry processing / reload result safely."
      );
      return true;
    }

    return false;
  }

  function startSubmissionPolling(submissionId, requestToken = feedbackRequestRef.current) {
    if (!submissionId) return;
    stopSubmissionPolling();
    const idNum = Number(submissionId);
    saveSubmissionRecoveryKey(idNum);
    setActiveSubmissionId(idNum);
    setProcessingFailed(false);
    setSubmitGuardMessage("Processing feedback...");
    setStatus("submitting");
    setLoadingFeedback(true);
    setFeedbackPhase("loading");
    submitInFlightRef.current = true;

    const pollOnce = async () => {
      if (requestToken !== feedbackRequestRef.current) return;
      const data = await fetchSubmissionStatus(idNum);
      if (requestToken !== feedbackRequestRef.current) return;
      if (!data) return;

      if (data.status === "processing") {
        setProcessingFailed(false);
        return;
      }

      if (data.status === "completed" && data.result_json && typeof data.result_json === "object") {
        if (hasRenderableEvaluationPayload(data.result_json)) {
          if (shouldRefreshW1L1FeedbackPayload(lessonContentId, data.result_json)) {
            const recovered = await recoverLatestSubmission({
              allowCompleted: true,
              surfaceFailed: false,
            });
            if (recovered) {
              if (refreshLlmUsage) refreshLlmUsage();
              return;
            }
            applyCompletedFeedback(data.result_json, `${API}/api/submissions/${encodeURIComponent(idNum)}`);
            if (refreshLlmUsage) refreshLlmUsage();
            return;
          } else {
            applyCompletedFeedback(data.result_json, `${API}/api/submissions/${encodeURIComponent(idNum)}`);
            if (refreshLlmUsage) refreshLlmUsage();
            return;
          }
        }
        if (process.env.NODE_ENV !== "production") {
          console.debug("W1L1 stale feedback remained stale after polling retry.", {
            lessonId: lessonContentId,
            sourceEndpoint: `${API}/api/submissions/${encodeURIComponent(idNum)}`,
          });
        }
        stopSubmissionPolling();
        clearRenderableFeedbackState();
        setProcessingFailed(true);
        setLoadingFeedback(false);
        setFeedbackPhase("idle");
        submitInFlightRef.current = false;
        setStatus("error");
        setSubmitGuardMessage(
          "Processing failed. Retry processing / reload result safely."
        );
        return;
      }

      if (data.status === "failed") {
        if (requestToken !== feedbackRequestRef.current) return;
        stopSubmissionPolling();
        clearRenderableFeedbackState();
        setProcessingFailed(true);
        setLoadingFeedback(false);
        setFeedbackPhase("idle");
        submitInFlightRef.current = false;
        setStatus("error");
        setSubmitGuardMessage(
          "Processing failed. Retry processing / reload result safely."
        );
      }
    };

    pollOnce();
    pollTimerRef.current = setInterval(pollOnce, 2500);
  }

  async function reloadResultSafely() {
    const latestRecovered = await recoverLatestSubmission({
      allowCompleted: true,
      surfaceFailed: false,
    });
    if (latestRecovered) return;

    if (activeSubmissionId) {
      const data = await fetchSubmissionStatus(activeSubmissionId);
      if (data?.status === "completed" && data?.result_json && typeof data.result_json === "object") {
        if (shouldRefreshW1L1FeedbackPayload(lessonContentId, data.result_json)) {
          const recovered = await recoverLatestSubmission({
            allowCompleted: true,
            surfaceFailed: false,
          });
          if (recovered) return;
        }
        applyCompletedFeedback(
          data.result_json,
          `${API}/api/submissions/${encodeURIComponent(activeSubmissionId)}`
        );
        return;
      }
      if (data?.status === "processing") {
        startSubmissionPolling(activeSubmissionId);
        return;
      }
    }

    const recovered = await recoverLatestSubmission({
      allowCompleted: true,
      surfaceFailed: false,
    });
    if (recovered) return;

    if (activeSubmissionId) {
      startSubmissionPolling(activeSubmissionId);
    }
  }

  useEffect(() => {
    if (!lessonLoadDone) return;

    let active = true;
    async function resumePendingSubmission() {
      let savedSubmissionId = null;
      let savedFeedbackVersion = "";
      try {
        const raw =
          localStorage.getItem(submissionRecoveryKey) ??
          localStorage.getItem(legacySubmissionRecoveryKey);
        const parsed = Number(raw);
        if (Number.isFinite(parsed) && parsed > 0) {
          savedSubmissionId = parsed;
        }
        savedFeedbackVersion =
          localStorage.getItem(submissionRecoveryVersionKey) ??
          localStorage.getItem(`${legacySubmissionRecoveryKey}:feedback_version`) ??
          "";
      } catch {
        savedSubmissionId = null;
        savedFeedbackVersion = "";
      }

      if (savedSubmissionId && active) {
        const recovered = await recoverLatestSubmission({ allowCompleted: true, surfaceFailed: false });
        if (recovered) {
          return;
        }
        if (!savedFeedbackVersion || savedFeedbackVersion !== CURRENT_FEEDBACK_UI_VERSION) {
          clearSubmissionRecoveryKey();
          await recoverLatestSubmission({ allowCompleted: true, surfaceFailed: false });
          return;
        }
        const data = await fetchSubmissionStatus(savedSubmissionId);
        if (!active) return;
        if (data?.status === "processing") {
          startSubmissionPolling(savedSubmissionId);
          return;
        }
        if (
          data?.status === "completed" &&
          data?.result_json &&
          typeof data.result_json === "object"
        ) {
          if (hasRenderableEvaluationPayload(data.result_json)) {
            if (shouldRefreshW1L1FeedbackPayload(lessonContentId, data.result_json)) {
              const recovered = await recoverLatestSubmission({
                allowCompleted: true,
                surfaceFailed: false,
              });
              if (recovered) {
                return;
              }
              applyCompletedFeedback(
                data.result_json,
                `${API}/api/submissions/${encodeURIComponent(savedSubmissionId)}`
              );
              return;
            } else {
              applyCompletedFeedback(
                data.result_json,
                `${API}/api/submissions/${encodeURIComponent(savedSubmissionId)}`
              );
              return;
            }
          }
          setActiveSubmissionId(savedSubmissionId);
          clearRenderableFeedbackState();
          setProcessingFailed(true);
          setSubmitGuardMessage(
            "Processing failed. Retry processing / reload result safely."
          );
          return;
        }
        if (data?.status === "failed") {
          setActiveSubmissionId(savedSubmissionId);
          clearRenderableFeedbackState();
          setProcessingFailed(true);
          setSubmitGuardMessage(
            "Processing failed. Retry processing / reload result safely."
          );
          return;
        }
        clearSubmissionRecoveryKey();
      }

      await recoverLatestSubmission({ allowCompleted: false, surfaceFailed: false });
    }

    resumePendingSubmission();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonLoadDone, submissionRecoveryKey, legacySubmissionRecoveryKey, lessonResetSignal]);

  async function submitFeedback() {
    if (readOnly) return;
    if (submitInFlightRef.current) return;
    retryEditingRef.current = false;
    setRetryEditing(false);
    const alreadyHasFeedback =
      status === "submitted" && (!!feedback || feedbackPhase !== "idle");
    if (alreadyHasFeedback) return;

    if (!isOnline) {
      setSubmitGuardMessage("Offline — keep writing. Reconnect to get feedback.");
      return;
    }

    if (!effectiveDraftText || effectiveDraftText.trim().length === 0) {
      setFeedback({
        creativity: "Please write something before submitting.",
        clarity: "",
        task_completion: "",
      });
      setAnnotations([]);
      setWritingSignalAnnotationSets({ basic: [], enriched: [] });
      setMentorTip(null);
      setMentorPlan(null);
      setMentorError(null);
      return;
    }

    if (isNarrativeCurveRewrite && narrativeCurveValidationMessage) {
      setSubmitGuardMessage(narrativeCurveValidationMessage);
      return;
    }

    const feedbackPayload = {
      student_id: studentId,
      week: weekNumber,
      lesson: lessonNumber,
      draft_text: effectiveDraftText,
      text: effectiveDraftText,
      structured_submission: hasStructuredSubmission
        ? structuredSubmission
        : null,
      dev_jump: devJump,
      attempt, // UI hint; backend decides
    };

    const fallbackToLegacyFeedback = async () => {
      const legacyRes = await fetch(`${API}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(feedbackPayload),
      });
      let legacyData = null;
      try {
        legacyData = await legacyRes.json();
      } catch (e) {
        console.error("Failed to parse JSON from legacy /feedback", e);
      }
      if (hasRenderableEvaluationPayload(legacyData)) {
        applyCompletedFeedback(legacyData, `${API}/feedback`);
        return true;
      }
      throw new Error(
        (legacyData && legacyData.detail) || "No feedback returned from server."
      );
    };

    setLoadingFeedback(true);
    const requestToken = beginFeedbackRequest();
    submitInFlightRef.current = true;
    setProcessingFailed(false);
    setFeedbackPhase("loading");
    setStatus("submitting");
    setSubmitGuardMessage("Processing feedback...");
    resetProgressPanelState();
    setLoadingFeedback(true);
    feedbackProgressStartedAtRef.current = Date.now();
    setConfirmedProgress(10);
    setDisplayedProgress(10);
    setDisplayedStage("Submission received");
    setDisplayedSubtitle("Your writing has been submitted successfully.");
    setDisplayedStatus("processing");
    feedbackSseSeqRef.current = 0;
    // If the learner asks for feedback, show the richest signal view once
    // annotations are available.
    setWritingSignalMode(WRITING_SIGNAL_MODES.ENRICHED);
    mentorSignalsAutoTriggeredRef.current = false;
    if (resetLlmUsage) {
      try {
        await resetLlmUsage();
      } catch {
        // non-blocking
      }
    }

    stopFeedbackProgressStream();

    try {
      const res = await fetch(`${API}/feedback/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User": getAuthUserHeader(),
        },
        body: JSON.stringify(feedbackPayload),
      });

      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        console.error("Failed to parse JSON from /feedback", e);
      }

      if (!res.ok || !data?.job_id) {
        if (requestToken !== feedbackRequestRef.current) return;
        await fallbackToLegacyFeedback();
        return;
      }

      const jobId = String(data.job_id || "").trim();
      if (!jobId) {
        throw new Error("No feedback job returned from server.");
      }

      if (typeof window.EventSource !== "function") {
        await fallbackToLegacyFeedback();
        return;
      }

      const source = new EventSource(`${API}/feedback/stream/${encodeURIComponent(jobId)}`);
      feedbackStreamRef.current = source;

      source.onmessage = async (event) => {
        if (requestToken !== feedbackRequestRef.current) {
          try {
            source.close();
          } catch {
            // non-blocking
          }
          return;
        }

        let eventData = null;
        try {
          eventData = JSON.parse(event.data);
        } catch (parseError) {
          console.error("Failed to parse SSE event", parseError);
          return;
        }

        if (!eventData || typeof eventData !== "object") return;
        const eventSeq = Number(eventData.seq);
        if (Number.isFinite(eventSeq) && eventSeq <= feedbackSseSeqRef.current) {
          return;
        }
        if (Number.isFinite(eventSeq)) {
          feedbackSseSeqRef.current = eventSeq;
        }
        console.log("SSE_EVENT", eventData.seq, eventData.progress, eventData.stage, eventData.status);
        setFeedbackProgressFromEvent(eventData);

        if (eventData.status === "completed" || eventData.status === "completed_with_fallback") {
          try {
            source.close();
          } catch {
            // non-blocking
          }
          if (feedbackStreamRef.current === source) {
            feedbackStreamRef.current = null;
          }
          const result = eventData.result;
          if (process.env.NODE_ENV !== "production") {
            console.debug("FEEDBACK_TERMINAL_SSE_RECEIVED", {
              lessonId: lessonContentId,
              status: eventData.status,
              hasResult: Boolean(result),
            });
          }
          if (!hasRenderableEvaluationPayload(result)) {
            clearRenderableFeedbackState();
            setFeedbackPhase("show");
            setStatus("error");
            setSubmitGuardMessage("No feedback returned from server.");
            return;
          }
          if (shouldRefreshW1L1FeedbackPayload(lessonContentId, result)) {
            const recovered = await recoverLatestSubmission({
              allowCompleted: true,
              surfaceFailed: false,
            });
            if (recovered) {
              if (refreshLlmUsage) refreshLlmUsage();
              return;
            }
          }
          setPendingFeedbackResult(result);
          setConfirmedProgress(100);
          setDisplayedStatus(eventData.status);
          if (feedbackProgressStartedAtRef.current === 0) {
            feedbackProgressStartedAtRef.current = Date.now();
          }
          if (refreshLlmUsage) refreshLlmUsage();
          return;
        }

        if (eventData.status === "failed" || eventData.status === "error") {
          try {
            source.close();
          } catch {
            // non-blocking
          }
          if (feedbackStreamRef.current === source) {
            feedbackStreamRef.current = null;
          }
          setLoadingFeedback(false);
          setFeedbackPhase("idle");
          setProcessingFailed(true);
          setStatus("error");
          setSubmitGuardMessage(
            eventData.error || "Processing failed. Please try again."
          );
        }
      };

      source.onerror = () => {
        // EventSource reconnects automatically; avoid false failure states here.
      };
    } catch (err) {
      if (requestToken !== feedbackRequestRef.current) return;
      console.error("Submit error:", err);
      try {
        await fallbackToLegacyFeedback();
      } catch (legacyError) {
        clearRenderableFeedbackState();
        setLoadingFeedback(false);
        setStatus("error");
        setFeedbackPhase("show");
        setSubmitGuardMessage(
          legacyError?.message ||
            "Connection interrupted. We’ll check if your feedback is still processing."
        );
      }
    } finally {
      if (requestToken === feedbackRequestRef.current && !pollTimerRef.current) {
        submitInFlightRef.current = false;
      }
      if (refreshLlmUsage) refreshLlmUsage();
    }
  }

  function handleGetFeedbackClick() {
    if (readOnly) return;
    if (!isOnline) {
      setSubmitGuardMessage("Offline — keep writing. Reconnect to get feedback.");
      return;
    }
    if (gateActive) {
      setSubmitGuardMessage("Watch the lesson video before submitting.");
      return;
    }
    if (showBelowMinConfirm) return;
    if (isNearEmptyDraft) {
      setSubmitGuardMessage("Please write a little more before getting feedback.");
      return;
    }
    if (!isLengthValid) {
      if (activeLengthHelperText) setSubmitGuardMessage(activeLengthHelperText);
      return;
    }
    if (!canGetFeedback) return;
    feedbackProgressScrollRequestedRef.current = true;
    submitFeedback();
  }

  function continueAfterMicroLesson() {
    if (nextLesson?.week && nextLesson?.lesson) {
      navigate(lessonPathFor(basePrefix, nextLesson.week, nextLesson.lesson));
      return;
    }
    fetchCurrentCursorTarget().then((cursor) => {
      navigate(lessonPathFor(basePrefix, cursor.week, cursor.lesson));
    });
  }

  // allow fail-forward after attempt 2 feedback OR explicit RETRY_LIMIT state
  async function goToNextLesson() {
    if (reflectionGateVisible) {
      await openPendingReflection();
      return;
    }

    // Always trust latest server cursor at click time.
    const liveCurrent = await fetchCurrentCursorTarget();
    const isPageBehindLive =
      liveCurrent.week > weekNumber ||
      (liveCurrent.week === weekNumber && liveCurrent.lesson > lessonNumber);
    if (isPageBehindLive) {
      const nextPath = buildLessonPath(liveCurrent);
      if (nextPath === location.pathname) return;
      navigate(nextPath);
      setTimeout(() => {
        if (window.location.pathname === location.pathname) {
          window.location.assign(nextPath);
        }
      }, 120);
      return;
    }

    const target = nextLesson || liveCurrent;
    const nextPath = buildLessonPath(target);
    if (nextPath === location.pathname) return;
    navigate(nextPath);
    setTimeout(() => {
      if (window.location.pathname === location.pathname) {
        window.location.assign(nextPath);
      }
    }, 120);
  }

  async function finishCourseView() {
    const marked = await markCurrentLessonCompleteBeforeReflection({
      studentId,
      week: weekNumber,
      lesson: lessonNumber,
    });
    if (!marked) {
      setStatus("error");
      setSubmitGuardMessage("Feedback must be stored before weekly reflection can open.");
      return;
    }
    navigate(`${basePrefix}/week-reflection/4`);
  }

  async function handleStartWeek() {
    try {
      setStartingWeek(true);
      setStartWeekError("");
      const url = `${API}/api/week-intro/complete?student_id=${encodeURIComponent(
        studentId
      )}&week=${encodeURIComponent(String(weekNumber))}`;
      const res = await fetch(url, { method: "POST" });
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.ok === false) {
        throw new Error((data && data.detail) || "Failed to start week.");
      }
      setWeekIntroRequired(false);
      const targetWeek = Number(data?.next_lesson?.week) || weekNumber;
      const targetLesson = Number(data?.next_lesson?.lesson) || 1;
      setReloadTick((n) => n + 1);
      navigate(`${basePrefix}/week/${targetWeek}/lesson/${targetLesson}`, { replace: true });
    } catch (err) {
      console.error("Failed to mark week intro complete:", err);
      setStartWeekError("Could not start week. Please try again.");
    } finally {
      setStartingWeek(false);
    }
  }

  const dashboardWeek = parseInt(String(dashboardData?.week ?? "0"), 10) || 0;
  const dashboardLesson = parseInt(String(dashboardData?.lesson ?? "0"), 10) || 0;
  const dashboardIntroRequired =
    Boolean(dashboardData?.week_intro_required) &&
    weekNumber === dashboardWeek &&
    lessonNumber === dashboardLesson &&
    lessonNumber === 1;
  const effectiveWeekIntroRequired = weekIntroRequired || dashboardIntroRequired;
  const effectiveWeekIntroData = weekIntroData || dashboardData?.week_intro || null;
  const navigationWeekOverview = location.state?.weekOverview || null;
  const forcedWeekOverviewData =
    navigationWeekOverview &&
    Number(navigationWeekOverview.week) === weekNumber &&
    navigationWeekOverview.weekIntro
      ? navigationWeekOverview.weekIntro
      : null;
  const shouldShowForcedWeekOverview = forceWeekOverview && !!forcedWeekOverviewData;

  useEffect(() => {
    if (mentorSignalsAutoTriggeredRef.current) return;
    const mentorVisibleNow = feedbackPhase === "show" && !!mentorTip;
    const hasSignalAnnotations =
      Array.isArray(annotations) && annotations.length > 0;
    if (!mentorVisibleNow || !hasSignalAnnotations) return;
    const signalAvailability = getWritingSignalModeAvailability(
      lessonContentId,
      annotations,
      writingSignalDisplayConfig.lessonSignalDisplay,
      writingSignalDisplayConfig.lessonSignalMap
    );
    const hasExtraEnriched = annotations.some(
      (ann) => String(ann?.source || "").toLowerCase() === "llm_enrichment"
    );
    setWritingSignalMode(
      hasExtraEnriched && signalAvailability.hasEnrichedExtra
        ? WRITING_SIGNAL_MODES.ENRICHED
        : WRITING_SIGNAL_MODES.BASIC
    );
    mentorSignalsAutoTriggeredRef.current = true;
  }, [
    feedbackPhase,
    mentorTip,
    annotations,
    lessonContentId,
    writingSignalDisplayConfig.lessonSignalDisplay,
    writingSignalDisplayConfig.lessonSignalMap,
  ]);

  function closeForcedWeekOverview() {
    navigate(`${basePrefix}/week/${weekNumber}/lesson/${lessonNumber}`, {
      replace: true,
      state: null,
    });
  }

  const scoreRendered = Number.isFinite(Number(feedback?.score))
    ? Number(feedback.score)
    : Number.isFinite(Number(rubricScore))
    ? Number(rubricScore)
    : null;
  const coachTextRendered = String(
    normalizeCoachFeedbackContract(feedback?.student_feedback)?.coach_feedback_paragraph || ""
  ).trim();
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (feedbackPhase !== "show" || !feedback) return;
    const score = scoreRendered;
    const coachFeedback = coachTextRendered;
    const result = feedback;
    const submission = {
      id: activeSubmissionId,
      status,
      sourceEndpoint: feedbackSourceEndpoint,
    };
    const progress = dashboardData;
    const lessonState = {
      feedbackPhase,
      learningState,
      passed,
      attempt,
      status,
    };
    console.log("RENDERED_FEEDBACK_SOURCE", {
      score,
      coachFeedback,
      result,
      submission,
      progress,
      lessonState,
      localStorageKeys: Object.keys(localStorage),
    });
  }, [
    feedbackPhase,
    feedback,
    scoreRendered,
    coachTextRendered,
    feedbackSourceEndpoint,
    lessonContentId,
    activeSubmissionId,
    status,
    dashboardData,
    learningState,
    passed,
    attempt,
  ]);

  // LOADING GUARD
  if (!dashboardData || (!effectiveWeekIntroRequired && !lessonContent && !lessonLoadDone)) {
    return (
      <div className="lesson-wrap">
        <div style={{ height: "48px" }} />
        <div className="lp-loading">Loading...</div>
      </div>
    );
  }

  if (effectiveWeekIntroRequired || shouldShowForcedWeekOverview) {
    const introDataToRender = shouldShowForcedWeekOverview
      ? forcedWeekOverviewData
      : effectiveWeekIntroData;
    return (
      <div className={`lesson-wrap ${inPortal ? "lesson-wrap--portal" : ""}`.trim()}>
        <div style={{ height: "48px" }} />
        <div className="lp-main">
          <WeekIntroPage
            weekNumber={weekNumber}
            weekIntro={introDataToRender}
            onStartWeek={shouldShowForcedWeekOverview ? closeForcedWeekOverview : handleStartWeek}
            starting={startingWeek}
            actionLabel={shouldShowForcedWeekOverview ? "Open Lesson" : "Start Week"}
          />
          {!shouldShowForcedWeekOverview && startWeekError ? (
            <div className="lp-box lp-warn" style={{ marginTop: 10 }}>
              {startWeekError}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (!lessonContent) {
    return (
      <div className={`lesson-wrap ${inPortal ? "lesson-wrap--portal" : ""}`.trim()}>
        <div style={{ height: "48px" }} />
        <div className="lp-main">
          <div className="lp-box lp-warn">
            {lessonLoadError || "Could not load lesson content."}
          </div>
        </div>
      </div>
    );
  }

  // -------- Video gating helpers --------
  const videoUrl = lessonContent?.introVideoUrl || null;
  const lessonHasVideo = !!videoUrl;
  const gateActive = lessonHasVideo && !videoWatched;
  const lessonLockedByVideo = lessonHasVideo && !videoWatched && !isAdminLessonTestMode;

  function isYouTube(url) {
    try {
      const u = new URL(url);
      const host = u.hostname.replace("www.", "");
      return (
        host === "youtu.be" ||
        host === "youtube.com" ||
        host === "m.youtube.com"
      );
    } catch {
      return false;
    }
  }

  async function markVideoWatched() {
    try {
      const res = await fetch(`${API}/api/video_watched`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student_id: studentId,
          week: weekNumber,
          lesson: lessonNumber,
        }),
      });
      await res.json().catch(() => ({}));
      setVideoWatched(true);
    } catch {
      setVideoWatched(true);
    }
  }

  function extractYouTubeId(url) {
    try {
      const u = new URL(url);
      const host = u.hostname.replace("www.", "");
      if (host === "youtu.be")
        return u.pathname.slice(1).split("?")[0].split("&")[0];
      if (host === "youtube.com" || host === "m.youtube.com") {
        const id = u.searchParams.get("v");
        return (id || "").split("?")[0].split("&")[0];
      }
      return null;
    } catch {
      return null;
    }
  }

  function renderLessonVideo(compact = false) {
    if (!videoUrl) return null;

    if (isYouTube(videoUrl)) {
      return (
        <div data-tour="lesson-video">
          <YouTubePlayer
            videoId={extractYouTubeId(videoUrl)}
            onEnded={markVideoWatched}
            className={compact ? "lp-video-compact" : ""}
            showLoadHelp={gateActive}
          />
        </div>
      );
    }

    return (
      <div data-tour="lesson-video">
        <div className={`lp-video ${compact ? "lp-video-compact" : ""}`.trim()}>
          <video
            controls
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              borderRadius: 8,
            }}
            onEnded={markVideoWatched}
          >
            <source src={videoUrl} />
          </video>
        </div>
      </div>
    );
  }

  // ----------------------------------------
  // Next + Retry + Feedback button gating
  // ----------------------------------------
  const hasFeedback =
    !!feedback && (feedbackPhase === "show" || feedbackPhase === "celebrate");
  // Only hard-complete state should hide lesson actions.
  // `showGraduation` can be set by backend for celebratory messaging and should not
  // suppress navigation/submission controls in non-complete states.
  const isFinalLessonAwaitingReflection = lessonContentId === "W4L4" && hasFeedback;
  const isGraduated = Boolean(courseCompleted) && !isFinalLessonAwaitingReflection;

  const hasCompletedAttempt2 = hasFeedback && attempt >= 2;
  const isAttempt2 = attempt >= 2;
  const lengthRule = parseLessonLengthRule(
    lessonContent?.lesson_data || lessonContent || {}
  );
  const minWordCount = Number(lengthRule.minWords || 0);
  const maxWordCount =
    Number.isFinite(Number(lengthRule.maxWords)) && Number(lengthRule.maxWords) > 0
      ? Number(lengthRule.maxWords)
      : null;
  const lengthRequirementLabel = String(lengthRule.displayLabel || "");
  const lessonLengthInstruction =
    hasStructuredSubmission
      ? ""
      : minWordCount > 0 && Number.isFinite(maxWordCount) && maxWordCount > minWordCount
      ? `Required length: ${minWordCount}\u2013${maxWordCount} words`
      : minWordCount > 0
      ? `Minimum length: ${minWordCount} words`
      : "";
  const structuredLengthInstruction =
    hasStructuredSubmission && structuredRequirementsText
      ? `Required sections: ${structuredRequirementsText}`
      : "";
  const isEmptyDraft = wordCount === 0;
  const isNearEmptyDraft = wordCount > 0 && wordCount < 10;
  const isBelowMinWordCount = minWordCount > 0 && wordCount < minWordCount;
  const isAboveMaxWordCount =
    Number.isFinite(maxWordCount) && maxWordCount > 0 && wordCount > maxWordCount;
  const narrativeCurveValidationMessage =
    isNarrativeCurveRewrite && isEmptyDraft
      ? "Write your rewritten version before submitting."
      : isNarrativeCurveRewrite && minWordCount > 0 && wordCount < minWordCount
      ? "Add a little more so the rewritten moment has room to feel deliberately restrained."
      : narrativeRewriteTooSimilar
      ? "Revise the passage so it creates a different emotional effect before submitting."
      : "";
  const isLengthValid = hasStructuredSubmission
    ? structuredSectionsValid
    : isNarrativeCurveRewrite
    ? !narrativeCurveValidationMessage
    : !isBelowMinWordCount && !isAboveMaxWordCount;
  const wordCountState =
    isBelowMinWordCount || isAboveMaxWordCount ? "red" : "green";
  const wordCountLabel =
    isBelowMinWordCount
      ? "Need more words"
      : isAboveMaxWordCount
      ? "Reduce length"
      : wordCountState === "green"
      ? "Good Length"
      : "Word count";
  const lengthHelperText = isBelowMinWordCount
    ? "Please write more to meet the lesson requirements."
    : isAboveMaxWordCount
    ? "Please reduce length to the lesson requirements."
    : "";
  const activeLengthHelperText = hasStructuredSubmission
    ? structuredBlockReason
    : isNarrativeCurveRewrite
    ? narrativeCurveValidationMessage
    : lengthHelperText;
  const wordCountBadgeStyle = {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    fontWeight: 700,
    lineHeight: 1.2,
    backgroundColor:
      isAboveMaxWordCount
        ? "#ef4444"
        : wordCountState === "red"
        ? "#fee2e2"
        : wordCountState === "green"
        ? "#dcfce7"
        : "#e5e7eb",
    border:
      isAboveMaxWordCount
        ? "1px solid #ef4444"
        : wordCountState === "red"
        ? "1px solid #fecaca"
        : wordCountState === "green"
        ? "1px solid #bbf7d0"
        : "1px solid #d1d5db",
    color: isAboveMaxWordCount ? "#ffffff" : "#111827",
  };
  const canGetFeedback =
    !(lessonClosed && !devJump) &&
    !loadingFeedback &&
    !hasFeedback &&
    feedbackPhase === "idle" &&
    isLengthValid;
  const feedbackEvaluationActive = isFeedbackEvaluationActive({ loadingFeedback, feedbackPhase });
  const feedbackButtonHardDisabled =
    loadingFeedback ||
    hasFeedback ||
    feedbackPhase !== "idle" ||
    (lessonClosed && !devJump) ||
    !isLengthValid;
  const getFeedbackDisabledTitle = hasFeedback
    ? "Feedback already received for this attempt."
    : !isOnline
    ? "Offline — reconnect to get feedback."
    : lessonClosed && !devJump
    ? "This lesson is closed because you have already progressed."
    : gateActive
    ? "Watch the lesson video before submitting."
    : !isLengthValid
    ? activeLengthHelperText
    : isEmptyDraft
    ? "Write something before getting feedback."
    : "";

  const failForward =
    hasFeedback &&
    (learningState === "RETRY_LIMIT" || (attempt >= 2 && !passed));

  const reflectionGateVisible =
    !devJump && (requiresWeekReflection || Boolean(dashboardData?.pending_weekly_reflection));
  const canGoNext =
    lessonClosed ||
    isViewingPastLesson() ||
    reflectionGateVisible ||
    (isFinalRecognitionLesson && hasFeedback) ||
    (hasFeedback &&
      (passed || learningState === "RETRY_LIMIT" || Boolean(nextLesson)));
  const canGoNextOnline = isOnline && canGoNext;

  const canRetryAttempt2 =
    !hasCompletedAttempt2 &&
    hasFeedback &&
    !passed &&
    attempt < 2 &&
    (!gatesEnforced || allowRepeat);
  const canOptionalImproveAttempt2 =
    !hasCompletedAttempt2 &&
    hasFeedback &&
    passed &&
    attempt < 2 &&
    Boolean(allowOptionalImprovementAttempt);
  const canShowWritingSignals =
    nerHighlightsEnabled &&
    feedbackPhase === "show" &&
    Array.isArray(annotations) &&
    annotations.length > 0;
  const basicWritingAnnotations = canShowWritingSignals
    ? getLessonSignalAnnotationsForMode(
        lessonContentId,
        WRITING_SIGNAL_MODES.BASIC,
        annotations,
        writingSignalDisplayConfig.lessonSignalDisplay,
        writingSignalDisplayConfig.lessonSignalMap
      )
    : [];
  const enrichedWritingAnnotations = canShowWritingSignals
    ? getLessonSignalAnnotationsForMode(
        lessonContentId,
        WRITING_SIGNAL_MODES.ENRICHED,
        annotations,
        writingSignalDisplayConfig.lessonSignalDisplay,
        writingSignalDisplayConfig.lessonSignalMap
      )
    : [];
  const signalModeAvailability = canShowWritingSignals
    ? getWritingSignalModeAvailability(
        lessonContentId,
        annotations,
        writingSignalDisplayConfig.lessonSignalDisplay,
        writingSignalDisplayConfig.lessonSignalMap
      )
    : { hasBasic: false, hasEnriched: false, hasEnrichedExtra: false };
  const hasBasicAnnotations = canShowWritingSignals && signalModeAvailability.hasBasic;
  const hasEnrichedAnnotations = canShowWritingSignals && signalModeAvailability.hasEnrichedExtra;
  const activeWritingSignalMode = canShowWritingSignals
    ? writingSignalMode
    : WRITING_SIGNAL_MODES.OFF;
  const visibleAnnotations =
    activeWritingSignalMode === WRITING_SIGNAL_MODES.ENRICHED
      ? hasEnrichedAnnotations
        ? enrichedWritingAnnotations
        : []
      : activeWritingSignalMode === WRITING_SIGNAL_MODES.BASIC
      ? basicWritingAnnotations
      : [];
  const isShowingWritingSignals =
    canShowWritingSignals &&
    activeWritingSignalMode !== WRITING_SIGNAL_MODES.OFF &&
    visibleAnnotations.length > 0;
  const showW4L4RewriteInstruction =
    lessonContentId === "W4L4" && Number(attempt) === 1 && !hasStructuredSubmission;
  const attemptBadge =
    isViewingPastLesson() ? (
      <span
        style={{
          background: "#16a34a",
          color: "white",
          padding: "4px 10px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 900,
          marginLeft: 10,
          letterSpacing: 0.3,
        }}
      >
        COMPLETED
      </span>
    ) : attempt >= 2 ? (
      <span
        style={{
          background: "#111827",
          color: "white",
          padding: "4px 10px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 900,
          marginLeft: 10,
          letterSpacing: 0.3,
        }}
      >
        ATTEMPT 2
      </span>
    ) : (
      <span
        style={{
          background: "#e5e7eb",
          color: "#111827",
          padding: "4px 10px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 900,
          marginLeft: 10,
          letterSpacing: 0.3,
        }}
      >
        ATTEMPT 1
      </span>
    );

  const maxAttempts = toFiniteNumber(attemptsAllowed) ?? 2;
  const feedbackStatus = getFeedbackStatus(
    feedback?.score,
    feedback?.pass_score,
    attempt,
    maxAttempts,
    passed,
    feedback?.display_frame,
    { suppressCompletionLabels: isFinalRecognitionLesson },
    lessonContent,
    lessonContentId
  );
  const feedbackStatusCopy = {
    heading: feedbackStatus.heading,
    supportingLine: feedbackStatus.supportingLine,
  };
  const lessonFooterCopy = getLessonFooterCopy({
    lessonMode:
      lessonContent?.lesson_mode && typeof lessonContent.lesson_mode === "object"
        ? lessonContent.lesson_mode
        : lessonContent?.lesson_data?.lesson_mode && typeof lessonContent.lesson_data.lesson_mode === "object"
        ? lessonContent.lesson_data.lesson_mode
        : {},
    evaluationStrategy:
      lessonContent?.evaluation_strategy && typeof lessonContent.evaluation_strategy === "object"
        ? lessonContent.evaluation_strategy
        : lessonContent?.lesson_data?.evaluation_strategy &&
          typeof lessonContent.lesson_data.evaluation_strategy === "object"
        ? lessonContent.lesson_data.evaluation_strategy
        : {},
    passed,
    attemptsRemaining: passed
      ? 0
      : Math.max(
          (toFiniteNumber(attemptsAllowed) ?? 2) - (toFiniteNumber(attempt) ?? 1),
          0
        ),
    scoreBand:
      feedback?.score != null
        ? getPerformanceBand(feedback.score, lessonContent || {}, lessonContentId)?.band || ""
        : "",
  });
  const storyCurveUpdate =
    feedback?.story_curve_update && typeof feedback.story_curve_update === "object"
      ? feedback.story_curve_update
      : null;
  const showEmotionCurveFocus =
    lessonContentId === "W4L4" &&
    feedbackPhase === "show" &&
    storyCurveUpdate?.target_curve === "emotion";
  const w4l4ChartOnboardingActive =
    lessonContentId === "W4L4" &&
    storyNextPressed &&
    !showEmotionCurveFocus &&
    !w4l4ChartIntroSeen &&
    w4l4ChartIntroPhase !== "complete";
  const w4l4CanSkipChartIntro = w4l4ChartOnboardingActive && Number(attempt) > 1;
  const w4l4ChartOnboardingShowsSection =
    w4l4ChartOnboardingActive &&
    ["intro_step_2", "intro_step_3"].includes(w4l4ChartIntroPhase);
  const w4l4ChartOnboardingShowsEmotion =
    w4l4ChartOnboardingActive && w4l4ChartIntroPhase === "intro_step_3";
  const w4l4ChartIntroState =
    lessonContentId === "W4L4" &&
    storyNextPressed &&
    !showEmotionCurveFocus &&
    !w4l4ChartIntroSeen
      ? w4l4ChartIntroPhase === "idle_waiting_for_view" ||
        w4l4ChartIntroPhase === W4L4_INTRO_CARD_PHASE
        ? "pending"
        : w4l4ChartIntroPhase === "intro_step_2"
        ? "section"
        : w4l4ChartIntroPhase === "intro_step_3"
        ? "emotion"
        : w4l4ChartIntroPhase === "intro_step_1_animating" ||
          w4l4ChartIntroPhase === "intro_step_1_done"
        ? "full"
        : "off"
      : "off";
  const w4l4ShouldMountStoryCurveChart =
    lessonContentId !== "W4L4" ||
    showEmotionCurveFocus ||
    w4l4ChartIntroSeen ||
    !storyNextPressed ||
    !["idle_waiting_for_view", W4L4_INTRO_CARD_PHASE].includes(w4l4ChartIntroPhase);
  const w4l4ChartFocusCurve =
    showEmotionCurveFocus || w4l4ChartOnboardingShowsEmotion ? "emotion" : "";
  const w4l4DimNonFocusCurves =
    showEmotionCurveFocus || w4l4ChartOnboardingShowsEmotion;
  const w4l4FocusSectionIndex =
    showEmotionCurveFocus || w4l4ChartOnboardingShowsSection ? 4 : null;
  const emotionCurveFeedback = storyCurveUpdate?.curves?.emotion || null;
  const emotionCurveFallbackMessage =
    showEmotionCurveFocus && storyCurveUpdate?.fallback_message
      ? String(storyCurveUpdate.fallback_message)
      : "";
  const revisedEmotionCurve = buildRevisedEmotionCurve(emotionCurveFeedback?.after_score);
  const emotionCurveOverrides =
    showEmotionCurveFocus && emotionCurveView === "after" && revisedEmotionCurve
      ? { emotion: revisedEmotionCurve }
      : {};
  const emotionCurveBadge =
    emotionCurveView === "after" ? "Revised Emotion" : "Original Emotion";
  const emotionBeforeLevel = String(emotionCurveFeedback?.before_level || "").trim();
  const emotionAfterLevel = String(emotionCurveFeedback?.after_level || "").trim();
  const emotionLevelUnchanged =
    emotionBeforeLevel &&
    emotionAfterLevel &&
    emotionBeforeLevel.toLowerCase() === emotionAfterLevel.toLowerCase();
  const emotionLevelSentence = emotionCurveFeedback
    ? emotionLevelUnchanged
      ? `Emotion remained ${emotionAfterLevel}.`
      : `Emotion moved from ${emotionBeforeLevel} to ${emotionAfterLevel}.`
    : "";
  const toggleEmotionCurveView = () => {
    setEmotionCurveView((current) => (current === "after" ? "before" : "after"));
    setEmotionCurveAnimationKey((current) => current + 1);
  };
  const scrollToWritingSignals = () => {
    setWritingSignalMode(
      hasEnrichedAnnotations ? WRITING_SIGNAL_MODES.ENRICHED : WRITING_SIGNAL_MODES.BASIC
    );
    setWritingSignalsReviewed(true);
    window.requestAnimationFrame(() => {
      const reducedMotion =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      writingAreaRef.current?.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "center",
      });
    });
  };
  const handleCurveStoryScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) {
      setStoryReadToEnd(true);
    }
  };
  const scrollToCurveChart = () => {
    setStoryNextPressed(true);
    setStoryCurveReviewed(true);
    window.requestAnimationFrame(() => {
      const reducedMotion =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      curveChartRef.current?.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "start",
      });
    });
  };
  const completeW4l4ChartIntro = () => {
    w4l4ChartIntroTimerRef.current.forEach((timerId) => window.clearTimeout(timerId));
    w4l4ChartIntroTimerRef.current = [];
    if (
      lessonContentId === "W4L4" &&
      Number(attempt) === 1 &&
      !w4l4StartWritingPreloadedRef.current
    ) {
      w4l4StartWritingPreloadedRef.current = true;
      setText(W4L4_SECTION_5_REWRITE_PASSAGE);
      setDirty(true);
      setStatus("typing");
    }
    setW4l4ChartIntroSeen(true);
    setW4l4ChartIntroPhase("complete");
    setW4l4VisiblePointCount(W4L4_CHART_POINT_COUNT);
    try {
      localStorage.setItem(W4L4_CHART_INTRO_SEEN_KEY, "1");
    } catch (error) {
      // non-blocking: replay prevention is best effort if storage is unavailable
    }
    window.requestAnimationFrame(() => {
      const reducedMotion =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      textareaRef.current?.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "center",
      });
      textareaRef.current?.focus({ preventScroll: true });
    });
  };
  const replayW4l4ChartIntro = () => {
    if (lessonContentId !== "W4L4") return;
    w4l4ChartIntroTimerRef.current.forEach((timerId) => window.clearTimeout(timerId));
    w4l4ChartIntroTimerRef.current = [];
    setW4l4ChartIntroSeen(false);
    setW4l4ChartIntroPhase(W4L4_INTRO_CARD_PHASE);
    setW4l4VisiblePointCount(0);
  };
  const resetW4l4ChartIntro = () => {
    if (lessonContentId !== "W4L4") return;
    w4l4ChartIntroTimerRef.current.forEach((timerId) => window.clearTimeout(timerId));
    w4l4ChartIntroTimerRef.current = [];
    try {
      localStorage.removeItem(W4L4_CHART_INTRO_SEEN_KEY);
    } catch (error) {
      // non-blocking: dev reset should still reset in-memory state
    }
    setW4l4ChartIntroSeen(false);
    setW4l4ChartIntroPhase("idle_waiting_for_view");
    setW4l4VisiblePointCount(0);
  };
  const startW4l4ChartIntroAnimation = () => {
    setW4l4VisiblePointCount(0);
    setW4l4ChartIntroPhase("intro_step_1_animating");
    const alignChartTop = () => {
      const reducedMotion =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      curveChartRef.current?.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "start",
      });
    };
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        alignChartTop();
        window.setTimeout(alignChartTop, 220);
      });
    });
  };
  const emotionFeedbackSummary =
    showEmotionCurveFocus && emotionCurveFeedback
      ? `${emotionLevelSentence} ${
          emotionCurveFeedback.rationale ||
          "The score is based on how strongly the rewritten section creates emotional intensity."
        }`
      : "";
  const focusedFeedback =
    showEmotionCurveFocus && emotionFeedbackSummary
      ? {
          ...feedback,
          creativity: emotionFeedbackSummary,
          clarity: "",
          task_completion: "",
        }
      : feedback;
  const showRetryCta = hasFeedback && feedbackStatus.primaryCta === "retry";
  // There is no separate non-scored revision endpoint, so a passed lesson
  // must not expose the legacy optional-improvement flag as Attempt 2.
  const showOptionalImproveCta = false;
  const showFinishCta = lessonContentId === "W4L4" && hasFeedback && canGoNext;
  const showNextCta =
    lessonContentId === "W4L4"
      ? showFinishCta
      : hasFeedback
      ? feedbackStatus.primaryCta === "next"
      : canGoNext;
  const lessonTitle =
    String(
      lessonContent?.topic ||
        lessonContent?.title ||
        lessonContent?.lesson_data?.topic ||
        lessonContent?.lesson_data?.title ||
        ""
    ).trim();
  const currentLessonState = Array.isArray(dashboardData?.lessons)
    ? dashboardData.lessons.find(
        (r) =>
          Number(r?.week) === Number(weekNumber) &&
          Number(r?.lesson) === Number(lessonNumber)
      )
    : null;
  const lessonIsCompleted = Boolean(
    !retryEditing && (currentLessonState?.closed || lessonClosed || isViewingPastLesson())
  );
  const showCompletionActions =
    feedbackPhase === "show" &&
    Boolean(feedback) &&
    (lessonIsCompleted || passed || failForward);
  const progressLessonId = lessonContentId || `W${weekNumber}L${lessonNumber}`;
  const pendingReflectionTargetWeek =
    Number(pendingReflectionWeek) ||
    Number(dashboardData?.pending_reflection_week) ||
    null;
  const resolvedNextLessonWeek = Number(
    nextLesson?.week || dashboardData?.next_lesson?.week || 0
  );
  const isFinalLessonOfWeek = Boolean(
    hasFeedback &&
      resolvedNextLessonWeek > Number(weekNumber) &&
      (reflectionGateVisible || pendingReflectionTargetWeek === Number(weekNumber))
  );
  const nextCtaLabel = showFinishCta
    ? "Final Reflection"
    : isFinalLessonOfWeek
    ? `Continue to Week ${Number(weekNumber)} Reflection →`
    : reflectionGateVisible
    ? "Weekly Reflection →"
    : "Next Lesson →";
  const nextCtaOnClick = showFinishCta
    ? finishCourseView
    : isFinalLessonOfWeek || reflectionGateVisible
    ? () => openPendingReflection()
    : goToNextLesson;
  const completedLessonNeedsReflection =
    lessonIsCompleted &&
    Boolean(
      pendingReflectionTargetWeek &&
        resolvedNextLessonWeek === Number(weekNumber) + 1 &&
        (requiresWeekReflection ||
          dashboardData?.pending_weekly_reflection ||
          pendingReflectionTargetWeek === weekNumber)
    );

  if (process.env.NODE_ENV !== "production") {
    console.debug(
      feedbackPhase === "show" && feedback
        ? "FEEDBACK_VIEW_SELECTED"
        : lessonIsCompleted && feedbackPhase === "idle" && !feedback
        ? "COMPLETED_GUARD_SELECTED"
        : "FEEDBACK_RENDER_STATE",
      {
        lessonId: lessonContentId,
        feedbackPhase,
        hasFeedback: Boolean(feedback),
        lessonIsCompleted,
      }
    );
  }

  if (courseCompleteView) {
    return (
      <main className="lp-course-complete-screen" aria-label="Course complete">
        <div className="lp-course-complete-content">
          <h1>Congratulations!</h1>
          <p>Course Complete</p>
        </div>
      </main>
    );
  }

  if (lessonIsCompleted && feedbackPhase === "idle" && !feedback) {
    const openPendingReflection = async () => {
      const marked = await markCurrentLessonCompleteBeforeReflection({
        studentId,
        week: weekNumber,
        lesson: lessonNumber,
      });
      if (!marked) {
        setStatus("error");
        setSubmitGuardMessage("Feedback must be stored before weekly reflection can open.");
        return;
      }
      navigate(`${basePrefix}/week-reflection/${pendingReflectionTargetWeek || weekNumber}`);
    };

    return (
        <CompletedLessonNotice
        week={weekNumber}
        lesson={lessonNumber}
        title={lessonTitle}
        primaryActionLabel={completedLessonNeedsReflection ? `Complete Week ${weekNumber} Reflection` : "View in Progress"}
        primaryAction={
          completedLessonNeedsReflection
            ? openPendingReflection
            : () =>
                navigate(`${progressPrefix}/dashboard_v2?lesson_id=${encodeURIComponent(progressLessonId)}`, {
                  state: { openLessonId: progressLessonId, scrollToLesson: true },
                })
        }
        secondaryActionLabel={completedLessonNeedsReflection ? "View in Progress" : "Back to Lessons"}
        secondaryAction={
          completedLessonNeedsReflection
            ? () =>
                navigate(`${progressPrefix}/dashboard_v2?lesson_id=${encodeURIComponent(progressLessonId)}`, {
                  state: { openLessonId: progressLessonId, scrollToLesson: true },
                })
            : () => {
                if (goToLessons) {
                  goToLessons();
                  return;
                }
                navigate(`/student/dashboard/${studentId}/lessons`);
              }
        }
      />
    );
  }

  return (
    <div
      className={`lesson-wrap ${inPortal ? "lesson-wrap--portal" : ""} ${
        focusMode ? "lesson-wrap--focus" : ""
      }`.trim()}
    >
      <div style={{ height: "48px" }} />

      <div className={`lp-main ${focusMode ? "lp-main--focus" : ""}`.trim()}>
        {!focusMode ? (
          <div className="lp-heading-row">
            <h2 className={`lp-h2 ${focusMode ? "lp-h2--focus" : ""}`.trim()}>
              <span className="lp-heading-title">
                Week {weekNumber}, Lesson {lessonNumber}: {lessonTitle}
              </span>
              {!focusMode ? attemptBadge : null}
            </h2>
          </div>
        ) : null}

        {renderLessonVideo(!lessonLockedByVideo)}
        {lessonHasVideo ? (
          <div
            className={`lp-video-lock-note ${
              lessonLockedByVideo ? "is-visible" : "is-hidden"
            }`.trim()}
            aria-hidden={!lessonLockedByVideo}
          >
            Please watch the full lesson video before proceeding. The task and writing area will unlock automatically when the video is complete.
          </div>
        ) : null}

        <div
          className={`lp-box lp-info lp-video-lock-banner ${
            lessonLockedByVideo ? "is-visible" : "is-hidden"
          }`.trim()}
          aria-hidden={!lessonLockedByVideo}
        >
          <div className="lp-video-lock-title">Lesson content locked</div>
          <div className="lp-video-lock-copy">
            Watch the video above to unlock the task and continue.
          </div>
        </div>

        <div
          className={`lp-lesson-reveal ${
            lessonLockedByVideo ? "is-locked" : "is-unlocked"
          }`.trim()}
          aria-hidden={lessonLockedByVideo}
        >
            {!focusMode ? (
              <>
                {/* Deterministic learning_state banners */}
                {isGraduated && (
                  <div className="lp-box lp-ok">
                    🎓 <strong>Course Complete</strong> You have completed the final lesson.
                  </div>
                )}
                {isFinalLessonAwaitingReflection && (
                  <div className="lp-box lp-ok">
                    Final Reflection is ready.
                  </div>
                )}
                {!isFinalLessonAwaitingReflection && !isGraduated && hasFeedback && learningState === "PASS" && (
                  <div className="lp-box lp-ok">✅ Passed. You can move to the next lesson.</div>
                )}
                {!isGraduated &&
                  reflectionGateVisible && (
                    <div className="lp-box lp-warn">
                      Weekly Reflection is required before Week {(pendingReflectionWeek || weekNumber) + 1} unlocks.
                    </div>
                  )}
                {!isGraduated && hasFeedback && learningState === "RETRY" && (
                  <div className="lp-box lp-warn">
                    🔁 Attempt 2 is available. Improve one thing — don’t rewrite everything.
                  </div>
                )}
                {!isGraduated && hasFeedback && learningState === "INTERVENTION" && (
                  <div className="lp-box lp-warn">
                    🧱 Intervention mode: follow the mentor steps exactly for your next attempt.
                  </div>
                )}
                {!isGraduated && hasFeedback && learningState === "RETRY_LIMIT" && (
                  <div className="lp-box lp-warn">
                    ⛔ Attempt limit reached. You can continue — we’ll revisit this skill later.
                  </div>
                )}

              </>
            ) : null}

            {isNarrativeCurveRewrite ? (
              <>
                <p
                  className={`lp-task ${focusMode ? "lp-task--focus" : ""} ${
                    hasStructuredSubmission ? "lp-task--structured" : ""
                  } lp-task--final`.trim()}
                  data-tour="lesson-task"
                >
                  <strong>Final Task: Reshaping Reader Experience</strong>
                  {"\n\n"}
                  {lessonContent?.task || ""}
                </p>
                {structuredLengthInstruction ? (
                  <div className="lp-structured-requirements">
                    {structuredLengthInstruction}
                  </div>
                ) : null}
                {lessonLengthInstruction ? (
                  <div
                    className={`lp-task-requirement ${
                      focusMode ? "lp-task-requirement--focus" : ""
                    }`.trim()}
                  >
                    {lessonLengthInstruction}
                  </div>
                ) : null}
                {!focusMode && feedbackPhase === "idle" ? renderLessonStartGuidance() : null}
                {!focusMode ? renderCoachTipsList(displayCoachTips) : null}
              </>
            ) : null}

            {isNarrativeCurveRewrite ? (
              <div className="lp-curve-lesson">
                <section className="lp-curve-intro">
                  <h3>Reshape the reader’s experience</h3>
                  <p>
                    Stories create emotional movement through pacing, tension, emotion,
                    mystery, dialogue, and detail. Study the whole story, then rewrite
                    one highlighted climax moment so the same events feel more distant
                    and emotionally restrained.
                  </p>
                </section>

                <details className="lp-curve-story-panel" open>
                  <summary>
                    Read or listen to{" "}
                    <em>{lessonContent?.story_asset?.title || "The Magic Building"}</em>{" "}
                    by {lessonContent?.story_asset?.author || "Shaanzae Shahid"}. Scroll through
                    the full text to continue and activate the Next button.
                  </summary>
                  {lessonContentId === "W4L4" ? (
                    <ThemedAudioPlayer
                      src="/audio/The_Magic_Building_v3.mp3"
                      title="Audio narration of The Magic Building"
                    />
                  ) : null}
                  <div
                    ref={curveStoryTextRef}
                    className="lp-curve-story-text"
                    onScroll={handleCurveStoryScroll}
                  >
                    {renderStoryWithHighlightedPassage(
                      lessonContent?.story_asset?.full_story || "",
                      originalRewritePassage
                    )}
                  </div>
                  <div className="lp-curve-story-next-row">
                    <button
                      type="button"
                      className={`lp-btn lp-curve-story-next-btn ${
                        storyReadToEnd ? "is-active" : "lp-disabled"
                      }`}
                      disabled={!storyReadToEnd}
                      onClick={scrollToCurveChart}
                    >
                      Next
                    </button>
                  </div>
                </details>

                {storyNextPressed ? (
                  <>
                    <figure
                      ref={curveChartRef}
                      className={`lp-curve-chart ${
                        lessonContent?.curve_chart?.type === "svg_component"
                          ? "lp-curve-chart--svg w4l4-curve-section"
                          : ""
                      }`.trim()}
                    >
                      {lessonContent?.curve_chart?.type === "svg_component" ? (
                        <div className="magic-curve-card">
                          {!w4l4ShouldMountStoryCurveChart ? (
                            <div className="lp-curve-intro-card">
                              <h3>Story Curves</h3>
                              <p>
                                A story can shape the reader's experience through tension,
                                emotion, pacing, mystery, dialogue, and sensory detail.
                              </p>
                              <button
                                type="button"
                                className="lp-btn lp-primary"
                                onClick={startW4l4ChartIntroAnimation}
                              >
                                Next: View Story Curves
                              </button>
                            </div>
                          ) : (
                            <StoryCurvesSvgChart
                              focusCurve={w4l4ChartFocusCurve}
                              dimNonFocusCurves={w4l4DimNonFocusCurves}
                              curveOverrides={emotionCurveOverrides}
                              focusSectionIndex={w4l4FocusSectionIndex}
                              animatedCurve={showEmotionCurveFocus ? "emotion" : ""}
                              animationKey={emotionCurveAnimationKey}
                              curveBadge={showEmotionCurveFocus ? emotionCurveBadge : ""}
                              chartIntroState={w4l4ChartIntroState}
                              introVisiblePointCount={w4l4VisiblePointCount}
                            />
                          )}
                        </div>
                      ) : (
                        <>
                        <img
                          src={lessonContent?.curve_chart?.src || lessonContent?.story_asset?.chart_image}
                          alt={
                            lessonContent?.curve_chart?.alt ||
                            "Narrative curve chart for The Magic Building"
                          }
                        />
                        </>
                      )}
                      {showEmotionCurveFocus ? (
                        <div className="lp-curve-focus-note">
                          <strong>Lesson Focus: Emotion</strong>
                          <span>
                            Only the Emotion curve was evaluated in this exercise. Other curves are shown for context.
                          </span>
                          <button
                            type="button"
                            className="lp-btn lp-ghost lp-curve-toggle"
                            onClick={toggleEmotionCurveView}
                          >
                            {emotionCurveView === "after"
                              ? "Show original Emotion curve"
                              : "Show revised Emotion curve"}
                          </button>
                        </div>
                      ) : null}
                      <figcaption>
                        {showEmotionCurveFocus
                          ? "Only the Emotion curve was evaluated in this exercise. Other curves are shown for context."
                          : "This chart shows how the original story moves through tension, emotion, pacing, mystery, dialogue, and sensory detail."}
                      </figcaption>
                      {w4l4ChartOnboardingActive &&
                      w4l4ChartIntroPhase !== W4L4_INTRO_CARD_PHASE ? (
                        <div className="lp-curve-onboarding-controls">
                          {w4l4ChartIntroPhase === "intro_step_1_done" ? (
                            <button
                              type="button"
                              className="lp-btn lp-primary"
                              onClick={() => setW4l4ChartIntroPhase("intro_step_2")}
                            >
                              Next: Focus on this Section
                            </button>
                          ) : null}
                          {w4l4ChartIntroPhase === "intro_step_2" ? (
                            <button
                              type="button"
                              className="lp-btn lp-primary"
                              onClick={() => setW4l4ChartIntroPhase("intro_step_3")}
                            >
                              Next: Focus on Emotion
                            </button>
                          ) : null}
                          {w4l4ChartIntroPhase === "intro_step_3" ? (
                            <button
                              type="button"
                              className="lp-btn lp-primary"
                              onClick={completeW4l4ChartIntro}
                            >
                              Start Writing
                            </button>
                          ) : null}
                          {w4l4CanSkipChartIntro && w4l4ChartIntroPhase !== "intro_step_3" ? (
                            <button
                              type="button"
                              className="lp-btn lp-ghost lp-curve-toggle"
                              onClick={completeW4l4ChartIntro}
                            >
                              Skip Intro
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                      {lessonContentId === "W4L4" && !showEmotionCurveFocus && !w4l4ChartOnboardingActive ? (
                        <div className="lp-curve-replay-row">
                          <button
                            type="button"
                            className="lp-curve-replay-link"
                            onClick={replayW4l4ChartIntro}
                          >
                            Replay Introduction
                          </button>
                          {process.env.NODE_ENV !== "production" ? (
                            <button
                              type="button"
                              className="lp-curve-replay-link"
                              onClick={resetW4l4ChartIntro}
                            >
                              Reset chart intro
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </figure>

                    {!w4l4ChartOnboardingActive ? (
                    <section className="lp-curve-challenge">
                      <div>
                        <span>Segment</span>
                        <strong>{rewriteChallenge?.segment_label || "Inside the Magic Building"}</strong>
                      </div>
                      <div>
                        <span>Story stage</span>
                        <strong>{rewriteChallenge?.story_stage || "Climax"}</strong>
                      </div>
                      <div>
                        <span>Focus curve</span>
                        <strong>{rewriteChallenge?.focus_curve || "emotional_intensity"}</strong>
                      </div>
                      <div>
                        <span>Original curve</span>
                        <strong>
                          {rewriteChallenge?.original_curve || "high emotional wonder and connection"}
                        </strong>
                      </div>
                      <div>
                        <span>Target curve</span>
                        <strong>{rewriteChallenge?.target_curve || "emotionally restrained and distant"}</strong>
                      </div>
                      <p>
                        <strong>Final task focus:</strong> Keep the same magical events, but change
                        how emotionally connected the reader feels to the moment.
                      </p>
                    </section>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}

            {(!isNarrativeCurveRewrite || (storyNextPressed && !w4l4ChartOnboardingActive)) ? (
              <>
            {!focusMode && shouldShowW4L4StoryCraftMap ? (
              <section className="lp-story-craft-reference" aria-label="Story craft map: The Magic Building">
                <div className="lp-lesson-block-title">Story craft map: The Magic Building</div>
                {showEmotionCurveFocus ? (
                  <div className="lp-curve-focus-note">
                    <strong>Lesson Focus: Emotion</strong>
                    <span>
                      Only the Emotion curve was evaluated in this exercise. Other curves are shown for context.
                    </span>
                  </div>
                ) : (
                  <p>
                    Use this as a visual reference while reflecting on how stories move
                    through tension, feeling, pace, mystery, dialogue, and sensory detail.
                  </p>
                )}
                {lessonContentId !== "W4L4" || w4l4ShouldMountStoryCurveChart ? (
                  <StoryCurvesSvgChart
                    focusCurve={w4l4ChartFocusCurve}
                    dimNonFocusCurves={w4l4DimNonFocusCurves}
                    curveOverrides={emotionCurveOverrides}
                    focusSectionIndex={w4l4FocusSectionIndex}
                    animatedCurve={showEmotionCurveFocus ? "emotion" : ""}
                    animationKey={emotionCurveAnimationKey}
                    curveBadge={showEmotionCurveFocus ? emotionCurveBadge : ""}
                    introVisiblePointCount={w4l4VisiblePointCount}
                  />
                ) : null}
                {showEmotionCurveFocus ? (
                  <button
                    type="button"
                    className="lp-btn lp-ghost lp-curve-toggle"
                    onClick={toggleEmotionCurveView}
                  >
                    {emotionCurveView === "after"
                      ? "Show original Emotion curve"
                      : "Show revised Emotion curve"}
                  </button>
                ) : null}
              </section>
            ) : null}

            {!isNarrativeCurveRewrite ? (
              <>
                <p
                  className={`lp-task ${focusMode ? "lp-task--focus" : ""} ${
                    hasStructuredSubmission ? "lp-task--structured" : ""
                  }`.trim()}
                  data-tour="lesson-task"
                >
                  <strong>Task:</strong> {lessonContent?.task || ""}
                </p>
                {structuredLengthInstruction ? (
                  <div className="lp-structured-requirements">
                    {structuredLengthInstruction}
                  </div>
                ) : null}
                {lessonLengthInstruction ? (
                  <div
                    className={`lp-task-requirement ${
                      focusMode ? "lp-task-requirement--focus" : ""
                    }`.trim()}
                  >
                    {lessonLengthInstruction}
                  </div>
                ) : null}
              </>
            ) : null}

            {!focusMode ? renderLessonStartGuidance() : null}
            {!focusMode && !isNarrativeCurveRewrite ? renderCoachTipsList(displayCoachTips) : null}

            <div
              ref={writingAreaRef}
              className={`lp-task-area ${focusMode ? "lp-task-area--focus" : ""} ${
                lessonLockedByVideo ? "lp-task-area--locked" : ""
              }`.trim()}
              style={{ position: "relative" }}
              data-tour="lesson-textarea"
            >
              {lessonLockedByVideo ? <div className="lp-task-area-lock" aria-hidden="true" /> : null}
              <div className="lp-task-area-head">
                {focusMode ? (
                  <button
                    type="button"
                    className="lp-btn lp-ghost lp-focus-toggle"
                    onClick={() => setFocusMode(false)}
                    aria-label="Exit Focus Mode"
                    aria-pressed={focusMode}
                  >
                    Exit Focus Mode
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="lp-btn lp-ghost lp-focus-toggle"
                      onClick={() => {
                        if (!lessonLockedByVideo) setFocusMode(true);
                      }}
                      disabled={lessonLockedByVideo}
                      aria-label="Enter Focus Mode"
                      aria-pressed={focusMode}
                      title={
                        lessonLockedByVideo
                          ? "Watch the lesson video before entering focus mode."
                          : undefined
                      }
                    >
                      Focus Mode
                    </button>
                  </>
                )}
              </div>
              {isShowingWritingSignals ? (
                <div className="lp-textarea lp-textarea-signals" aria-live="polite">
                  <HighlightedCreativeText
                    text={text}
                    annotations={visibleAnnotations}
                    showInlineHighlights={nerHighlightsEnabled === true}
                  />
                </div>
              ) : hasStructuredSubmission ? (
                <div className="lp-structured-submission">
                  {structuredSectionStates.map((section) => {
                    const value = String(
                      section.read_only
                        ? section.content || structuredSubmission?.[section.key] || ""
                        : structuredSubmission?.[section.key] || ""
                    );
                    const requiredLabel = section.rangeLabel || "No word requirement";
                    const sectionStatusText = section.read_only ? "Read-only" : section.statusText;
                    const hideStructuredChrome =
                      isFinalRecognitionLesson && section.key === "final_reflection";
                    const showSectionMeta =
                      !hideStructuredChrome && Boolean(section.rangeLabel || section.guidanceText);
                    return (
                      <div className="lp-structured-section" key={section.key}>
                        {!hideStructuredChrome ? (
                          <div className="lp-structured-section-head">
                            <label
                              className="lp-structured-label"
                              htmlFor={`submission-section-${section.key}`}
                            >
                              {section.label}
                            </label>
                          </div>
                        ) : null}
                        {!hideStructuredChrome && section.description ? (
                          <div className="lp-structured-description">
                            {section.description}
                          </div>
                        ) : null}
                        {section.read_only ? (
                          <div
                            id={`submission-section-${section.key}`}
                            className="lp-readonly-draft"
                          >
                            {value}
                          </div>
                        ) : (
                          <textarea
                            id={`submission-section-${section.key}`}
                            aria-label={hideStructuredChrome ? "Final reflection" : undefined}
                            rows={6}
                            value={value}
                            onChange={(e) =>
                              handleStructuredSectionChange(section.key, e.target.value)
                            }
                            disabled={lessonLockedByVideo}
                            placeholder={section.placeholder || "Write this section..."}
                            className="lp-textarea lp-textarea--section"
                          />
                        )}
                        {showSectionMeta ? (
                          <div className="lp-structured-section-meta">
                            <span className="lp-structured-count">
                              {section.words} words · Required: {requiredLabel} ·
                            </span>
                            <span
                              className={`lp-structured-badge lp-structured-badge--${section.status}`}
                            >
                              {sectionStatusText}
                            </span>
                            {section.guidanceText ? (
                              <span className="lp-structured-guidance">
                                {section.guidanceText}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                {isNarrativeCurveRewrite ? (
                  <label className="lp-rewrite-label" htmlFor="lesson-main-textarea">
                    Your rewritten version
                  </label>
                ) : null}
                {showW4L4RewriteInstruction ? (
                  <div className="lp-emotion-hint-note">
                    Rewrite the same passage so the emotional experience lowers and becomes much more distant and restrained.
                  </div>
                ) : null}
                  <textarea
                    id="lesson-main-textarea"
                    ref={textareaRef}
                    rows={8}
                    value={text}
                    onChange={handleTextChange}
                    onPaste={handleTextPaste}
                    disabled={lessonLockedByVideo}
                    placeholder={
                      isNarrativeCurveRewrite
                        ? "Rewrite the highlighted moment so the emotional experience feels more distant, restrained, and emotionally detached while preserving the same magical events..."
                        : isFinalRecognitionLesson
                        ? "What changed in the way you think about writing?\n\nWhich moment in your writing felt most honest?\n\nWhat do you notice now that you would not have noticed earlier?\n\nWhat kind of writer do you want to become next?"
                        : "Write your response..."
                    }
                    className="lp-textarea"
                  />
                </>
              )}
            </div>
            {feedbackEvaluationActive ? (
              <FeedbackProgressSlot
                active={feedbackEvaluationActive}
                progress={displayedProgress}
                stage={displayedStage}
                subtitle={displayedSubtitle}
                status={displayedStatus}
                slow={feedbackSlow}
                scrollRef={feedbackProgressScrollRef}
              />
            ) : null}
            {submitGuardMessage ? (
              <div className="lp-box lp-warn" role="alert" style={{ marginTop: 10 }}>
                {submitGuardMessage}
              </div>
            ) : null}
            {pasteNotice ? (
              <div
                className="lp-box lp-warn"
                style={{ marginTop: 10, fontSize: 13, padding: "8px 10px" }}
              >
                {pasteNotice}
              </div>
            ) : null}
            {processingFailed && activeSubmissionId ? (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="lp-btn lp-ghost"
                  onClick={reloadResultSafely}
                  disabled={!isOnline}
                  title={!isOnline ? "Offline — reconnect to reload safely." : ""}
                >
                  Reload Result Safely
                </button>
              </div>
            ) : null}

            <div
              className={`lp-status ${focusMode ? "lp-status--focus" : ""}`.trim()}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
            >
              <span>
                Draft status: {statusLabelMap[status] || status}
              </span>
              <div
                style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
              >
                <div
                  className={`lp-writing-signals-control ${
                    canShowWritingSignals ? "" : "lp-disabled"
                  }`}
                  data-tour="lesson-signals"
                  role="group"
                  aria-label="Writing signals display mode"
                  title={
                    canShowWritingSignals
                      ? "Choose how much writing-signal annotation to show."
                      : "Writing signals will appear after feedback."
                  }
                >
                  {[
                    { mode: WRITING_SIGNAL_MODES.BASIC, label: "Basic" },
                    { mode: WRITING_SIGNAL_MODES.ENRICHED, label: "Enriched" },
                    { mode: WRITING_SIGNAL_MODES.OFF, label: "Off" },
                  ].map((item) => {
                    const unavailable =
                      (item.mode === WRITING_SIGNAL_MODES.BASIC && !hasBasicAnnotations) ||
                      (item.mode === WRITING_SIGNAL_MODES.ENRICHED && !hasEnrichedAnnotations);
                    const selected = activeWritingSignalMode === item.mode;
                    return (
                      <button
                        key={item.mode}
                        type="button"
                        className={`lp-writing-signals-segment ${
                          selected ? "is-active" : ""
                        }`}
                        onClick={() => {
                          if (!unavailable) setWritingSignalMode(item.mode);
                        }}
                        disabled={!canShowWritingSignals || unavailable}
                        aria-pressed={selected}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
                <div
                  data-tour="lesson-wordcount"
                  style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
                >
                  {hasStructuredSubmission ? (
                    <>
                      <span className="lp-structured-status-summary">
                        {structuredStatusText}
                      </span>
                      <span className="lp-length-requirement">
                        Total: {structuredTotalWordCount} words
                      </span>
                    </>
                  ) : isNarrativeCurveRewrite ? (
                    <>
                      <span>{`${narrativeRewriteWordCount} words`}</span>
                      <span style={wordCountBadgeStyle}>{wordCountLabel}</span>
                      <span className="lp-length-requirement">
                        Target: 150-300 words · Minimum: 120 words
                      </span>
                    </>
                  ) : (
                    <>
                      <span>{`${wordCount} words`}</span>
                      <span style={wordCountBadgeStyle}>{wordCountLabel}</span>
                      <span className="lp-length-requirement">{lengthRequirementLabel}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            {feedbackPhase === "show" && feedback && (
              <div data-tour="lesson-feedback">
                <FeedbackCard
                  feedback={focusedFeedback}
                  coach={coach}
                  lessonContent={lessonContent}
                  rubricScore={rubricScore}
                  passed={passed}
                  attempt={attempt}
                  attemptsAllowed={attemptsAllowed}
                  allowRepeat={allowRepeat}
                  statusCopy={feedbackStatusCopy}
                  displayFrame={isFinalRecognitionLesson ? { score_label: "Reflection" } : feedback?.display_frame}
                />
                {emotionCurveFallbackMessage ? (
                  <div className="lp-box lp-emotion-curve-feedback">
                    <div className="lp-coach-feedback-head">
                      <h3 className="lp-coach-feedback-title">Lesson Focus: Emotion</h3>
                    </div>
                    <p className="lp-emotion-curve-rationale">{emotionCurveFallbackMessage}</p>
                  </div>
                ) : showEmotionCurveFocus && emotionCurveFeedback ? (
                  <div className="lp-box lp-emotion-curve-feedback">
                    <div className="lp-coach-feedback-head">
                      <h3 className="lp-coach-feedback-title">Lesson Focus: Emotion</h3>
                      <p className="lp-coach-feedback-copy">
                        Only the Emotion curve was evaluated in this exercise. Other curves are shown for context.
                      </p>
                    </div>
                    <div className="lp-emotion-curve-summary">
                      <div>
                        {emotionLevelUnchanged ? (
                          <>
                            Emotion remained <strong>{emotionAfterLevel}</strong>.
                          </>
                        ) : (
                          <>
                            Emotion moved from <strong>{emotionBeforeLevel}</strong>{" "}
                            to <strong>{emotionAfterLevel}</strong>.
                          </>
                        )}
                      </div>
                      {!emotionLevelUnchanged ? (
                      <div>
                        Score: <strong>{emotionCurveFeedback.before_score}</strong> to{" "}
                        <strong>{emotionCurveFeedback.after_score}</strong>
                      </div>
                      ) : null}
                    </div>
                    {emotionCurveFeedback.rationale ? (
                      <p className="lp-emotion-curve-rationale">{emotionCurveFeedback.rationale}</p>
                    ) : null}
                    {Array.isArray(emotionCurveFeedback.evidence) &&
                    emotionCurveFeedback.evidence.length > 0 ? (
                      <blockquote className="lp-emotion-curve-evidence">
                        “{emotionCurveFeedback.evidence[0]}”
                      </blockquote>
                    ) : null}
                  </div>
                ) : null}
                {feedback.student_feedback && !showEmotionCurveFocus && (
                  <div
                    className="lp-box"
                    style={{
                      marginTop: 12,
                      border: "1px solid #e5e7eb",
                      background: "#ebf8fb",
                      color: "#111827",
                    }}
                  >
                    <div className="lp-coach-feedback-head">
                      <h3 className="lp-coach-feedback-title">Coach feedback</h3>
                      <p className="lp-coach-feedback-copy">
                        {(() => {
                          const coachBlock = normalizeCoachFeedbackContract(feedback.student_feedback);
                          if (!coachBlock.coach_available) return "Your evaluation is complete.";
                          return {
                            failed_retry: "Use this to improve your next draft.",
                            passed_close: "Use this to strengthen your writing.",
                            passed_well: "Use this in your future writing.",
                            failed_final: "Use this in your future lessons and writing.",
                          }[coachBlock.outcome_mode] || "Use this to improve your next draft.";
                        })()}
                      </p>
                    </div>
                    <div style={{ marginTop: "12px", lineHeight: 1.6, fontSize: "1.05rem" }}>
                      {(() => {
                        const coachBlock = normalizeCoachFeedbackContract(feedback.student_feedback);
                        if (!coachBlock.coach_available) {
                          return (
                            <>
                              <p style={{ color: "#1f2937", margin: 0 }}>
                                {coachBlock.coach_notice || "Coach feedback is temporarily unavailable. Your score and retry status are still valid."}
                              </p>
                            </>
                          );
                        }
                        if (coachBlock.encouragement_message || coachBlock.coach_feedback_paragraph || coachBlock.paragraphs.length) {
                          return (
                            <>
                              {coachBlock.encouragement_message ? (
                                <div
                                  style={{
                                    marginBottom: "12px",
                                    padding: "10px 12px",
                                    borderRadius: "10px",
                                    background: "#f8f5ee",
                                    color: "#5b3f00",
                                    border: "1px solid #ead7a1",
                                    fontSize: "0.98rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {coachBlock.encouragement_message}
                                </div>
                              ) : null}
                              {coachBlock.paragraphs.length ? (
                                <div className="coach-feedback-body lp-coach-feedback-paragraphs">
                                  {coachBlock.paragraphs.map((paragraph, index) => (
                                    <p key={`${index}-${paragraph.slice(0, 24)}`}>
                                      {paragraph}
                                    </p>
                                  ))}
                                </div>
                              ) : coachBlock.coach_feedback_paragraph ? (
                                <div className="coach-feedback-body lp-coach-feedback-paragraphs">
                                  <p>{coachBlock.coach_feedback_paragraph}</p>
                                </div>
                              ) : null}
                            </>
                          );
                        }
                        if (coachBlock.coach_feedback_paragraph) {
                          return (
                            <div className="coach-feedback-body lp-coach-feedback-paragraphs">
                              {coachBlock.paragraphs.map((paragraph, index) => (
                                <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>
                              ))}
                            </div>
                          );
                        }
                        const workedText = coachBlock.what_worked || "You have a workable start with at least one clear idea.";
                        const holdingBackText =
                          coachBlock.what_holding_back ||
                          "The current draft does not yet match this lesson's main requirement.";
                        const guidanceText =
                          coachBlock.next_step_message ||
                          "Go back to the lesson guide, then apply one focused revision move before Attempt 2.";
                        if (process.env.NODE_ENV !== "production" && !coachBlock.next_step_message) {
                          console.warn("COACH_RENDER_WARN: guidance block fallback rendered");
                        }
                        return (
                          <>
                            <h3 style={{ color: "#111827", marginBottom: 10 }}>What’s working well</h3>
                            <div style={{ color: "#166534", marginBottom: 6 }}>✅ {workedText}</div>

                            <h3 style={{ color: "#111827", marginTop: 20, marginBottom: 10 }}>
                              {isFinalRecognitionLesson ? "Growth pattern" : "What’s holding it back"}
                            </h3>
                            <div style={{ color: "#92400e", marginBottom: 6 }}>
                              {isFinalRecognitionLesson ? holdingBackText : `⚠️ ${holdingBackText}`}
                            </div>

                            <h3 style={{ color: "#111827", marginTop: 20, marginBottom: 10 }}>
                              Coach guidance
                            </h3>
                            <div style={{ color: "#1f2937", marginBottom: 6 }}>{guidanceText}</div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
                <AdminEvaluationDetails details={feedback.admin_details} currentUser={canonicalAuthUser} />
              </div>
            )}

            {feedbackPhase === "show" && feedback && (canShowWritingSignals || lessonContentId === "W4L4") ? (
              <div className="lp-post-feedback-discovery" aria-label="Review post-feedback evidence">
                {canShowWritingSignals ? (
                  <button
                    type="button"
                    className="lp-btn lp-secondary-action"
                    onClick={scrollToWritingSignals}
                  >
                    {writingSignalsReviewed ? "✓ Writing Signals Reviewed" : "Review Writing Signals"}
                  </button>
                ) : null}
                {lessonContentId === "W4L4" ? (
                  <button
                    type="button"
                    className="lp-btn lp-secondary-action"
                    onClick={scrollToCurveChart}
                  >
                    {storyCurveReviewed ? "✓ Story Curve Reviewed" : "Review Story Curve"}
                  </button>
                ) : null}
              </div>
            ) : null}

            {feedbackPhase === "show" && renderMentorBox()}

            {showCompletionActions ? (
              <div className="lp-box lp-completion-actions" aria-label="Completion actions">
                <h2>Lesson completed</h2>
                <p>Your feedback is ready to review. Continue when you are ready.</p>
                <div className="lp-actions">
                  {showNextCta ? (
                    <button
                      type="button"
                      onClick={nextCtaOnClick}
                      disabled={showFinishCta ? false : !canGoNextOnline}
                      className="lp-btn lp-primary lp-next-lesson-btn"
                    >
                      {nextCtaLabel}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="lp-btn lp-ghost"
                    onClick={() =>
                      navigate(`${progressPrefix}/dashboard_v2?lesson_id=${encodeURIComponent(progressLessonId)}`, {
                        state: { openLessonId: progressLessonId, scrollToLesson: true },
                      })
                    }
                  >
                    Review Progress
                  </button>
                </div>
              </div>
            ) : null}

            {!isGraduated && <div className="lp-actions">
              {!hasFeedback && (
                <div className="lp-feedback-action-wrap">
                  <button
                    onClick={handleGetFeedbackClick}
                    disabled={feedbackButtonHardDisabled || lessonLockedByVideo}
                    title={getFeedbackDisabledTitle}
                    className={`lp-btn ${
                      feedbackButtonHardDisabled || lessonLockedByVideo ? "lp-disabled" : "lp-primary"
                    } ${!isOnline ? "lp-feedback-offline-disabled" : ""}`}
                    data-tour="lesson-submit"
                  >
                    {loadingFeedback
                      ? "Submitting…"
                      : isAttempt2
                      ? "Get Feedback (Attempt 2)"
                      : "Get Feedback"}
                  </button>
                  <div className="lp-feedback-offline-hint" aria-live="polite">
                    {!isOnline
                      ? "Offline — keep writing. Reconnect to get feedback."
                      : !isLengthValid
                      ? activeLengthHelperText
                      : ""}
                  </div>
                </div>
              )}

              {(showRetryCta || showOptionalImproveCta) && (
                <div className="lp-retry-cta-wrap">
                  <button
                    onClick={handleRetryOrImproveClick}
                    disabled={showOptionalImproveCta ? !canOptionalImproveAttempt2 : !canRetryAttempt2}
                    title={
                      !hasFeedback
                        ? "Get feedback first."
                        : passed && !showOptionalImproveCta
                        ? "You passed — no retry needed."
                        : gatesEnforced && !allowRepeat && !showOptionalImproveCta
                        ? "Retry is not allowed for this lesson."
                        : ""
                    }
                    className={`lp-btn ${showOptionalImproveCta ? "lp-ghost" : "lp-primary"} ${
                      showOptionalImproveCta
                        ? canOptionalImproveAttempt2
                          ? ""
                          : "lp-disabled"
                        : canRetryAttempt2
                        ? ""
                        : "lp-disabled"
                    }`}
                    data-tour="lesson-retry"
                  >
                    {showOptionalImproveCta ? improvementAttemptLabel : "Retry Attempt 2"}
                  </button>
                  <span className="lp-retry-helper-text">
                    {lessonFooterCopy}
                  </span>
                </div>
              )}

              {showNextCta && !showCompletionActions && (
                <button
                  onClick={nextCtaOnClick}
                  disabled={showFinishCta ? false : !canGoNextOnline}
                  title={
                    showFinishCta
                      ? ""
                      : !isOnline
                      ? "Offline — reconnect to continue."
                      : reflectionGateVisible
                      ? "Weekly Reflection is required before the next week unlocks."
                      : gatesEnforced && !passed && !failForward && !isFinalRecognitionLesson
                      ? "Attempt 2 first (or pass the lesson)."
                      : learningState === "RETRY_LIMIT"
                      ? "Attempt limit reached — continue, and we’ll revisit later."
                      : ""
                  }
                  className={`lp-btn lp-primary ${
                    showFinishCta || canGoNextOnline ? "" : "lp-disabled"
                  } lp-next-lesson-btn`}
                >
                  {nextCtaLabel}
                </button>
              )}
            </div>}

            {microLesson && !isGraduated && !focusMode && (
              <MicroLessonCard
                micro={microLesson}
                onContinue={continueAfterMicroLesson}
              />
            )}
              </>
            ) : null}
        </div>
      </div>

      {showBelowMinConfirm ? (
        <div
          className="lp-submit-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowBelowMinConfirm(false);
          }}
          role="presentation"
        >
          <div className="lp-submit-modal" role="dialog" aria-modal="true" aria-labelledby="lp-below-min-title">
            <h3 id="lp-below-min-title" className="lp-submit-modal-title">
              Your writing is shorter than recommended
            </h3>
            <p className="lp-submit-modal-body">
              This task expects at least {minWordCount} words so you can fully express your ideas.
              You can still get feedback now, but your score may be affected.
            </p>
            <div className="lp-submit-modal-actions">
              <button
                ref={belowMinPrimaryRef}
                type="button"
                className="lp-btn lp-primary"
                onClick={() => setShowBelowMinConfirm(false)}
              >
                Continue Writing
              </button>
              <button
                type="button"
                className="lp-btn lp-ghost"
                onClick={() => {
                  setShowBelowMinConfirm(false);
                  submitFeedback();
                }}
              >
                Get Feedback Anyway
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showImproveDraftConfirm ? (
        <div
          className="lp-submit-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowImproveDraftConfirm(false);
          }}
          role="presentation"
        >
          <div
            className="lp-submit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lp-improve-draft-title"
          >
            <h3 id="lp-improve-draft-title" className="lp-submit-modal-title">
              Improve This Draft?
            </h3>
            <div className="lp-submit-modal-body">
              <p>You already passed this lesson.</p>
              <p>
                Improving the draft will reopen editing so you can strengthen your writing further,
                but your completed progress will remain saved.
              </p>
            </div>
            <div className="lp-submit-modal-actions">
              <button
                type="button"
                className="lp-btn lp-primary"
                onClick={goToRetryAttempt2}
              >
                Continue Improving
              </button>
              <button
                type="button"
                className="lp-btn lp-ghost"
                onClick={() => setShowImproveDraftConfirm(false)}
              >
                Stay on Completed Lesson
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
}
